/**
 * DoctorCabinetPage — КАБИНЕТ ВРАЧА.
 * Структура 1:1 с UX-демо «Cortivox — финал 25.10» (cortivox-demo.html, вид «Врач»):
 *   сайдбар w-64 (Пациенты / Запросы / Архив + блок врача) на lg, пилюли на мобильном;
 *   секция «Пациенты»: 4 стат-карты, поиск, фильтры, карточки пациентов, «Добавить пациента»;
 *   карточка пациента (Обзор / Шкалы / ИПР / Отчёты: под-вкладки Шкалы и Тренажер).
 * Также экспортирует PatientCardView — карточку, которую демо открывает по маршруту
 * /patient с location.state.patientProfile (см. PatientCabinetPage); state.initialTab
 * открывает карточку сразу на вкладке ИПР/Отчёты (кнопки на плашке пациента).
 * ПРАВКИ 26.10 (унификация с лендингом): логотип Cortivox.ai 1:1 с лендингом,
 * кнопки-пилюли (добавить пациента / выйти / принять / провести и т.д.),
 * чёрные заголовки вместо зелёных, «Требуют внимания» — янтарная плашка,
 * на плашке пациента кнопки ИПР и Отчёты (быстрый тест убран — врач его не запускает),
 * в ИПР — кнопка «Добавить упражнение» со списком-галочками,
 * отчёты по шкалам тянутся из GET /api/reports (см. listScaleReports).
 * ОТЧЁТЫ-ШКАЛЫ 26.10 (v4): клик по оценке открывает ПОЛНОСТРАНИЧНЫЙ отчёт
 * (просьба врача: «отчёты должны раскрываться на всю страницу»). Секции — те
 * же, что на «выписке» после «Провести»: шапка-выписка (балл крупно, порог
 * нормы и вердикт, дата, пациент), плашки «предварительные данные» и возрастной
 * поправки, клиническая интерпретация (сценарии, гипотезы, память, бдительность,
 * MIS, VLOM), ЛУЧЕВАЯ ДИАГРАММА (recharts) + домены полосами, и таблица
 * «Ответы пациента» (сравнение с эталоном + чеклисты куба/часов с пересчётом
 * через rateReport) — таблица строится, когда сервер присылает rawAnswers в
 * строке списка (задание Клоду №2). JSON-блок
 * «Полные данные отчёта» убран по просьбе врача (v4). Поля движка:
 * scoreResult.totalScore (scaleScoreOf, включая ЧИСЛОВОЙ scoreResult NIHSS),
 * ageAdjustedCutoff, isPendingManualReview; FAB — в SCALE_LABELS.
 * v5: СТРУП/SDMT — полный разбор теста скорости в отчёте кабинета: сервер кладёт
 * его в scoreResult.stroop (scoreResult.sdmt), рендерим ЕГО ЖЕ компонентом,
 * что и пациенту после теста — SpeedTestReport (../patient-records/),
 * подставив raw и onExit=«Назад к отчётам». Балл Струпа ищется и во вложенном
 * отчёте (scaleScoreOf).
 * v6: подсказка «таблица появится после дополнения сервера» УБРАНА ПОЛНОСТЬЮ
 * по просьбе врача — пока rawAnswers не приходят, отчёт просто не показывает
 * секцию ответов, без пояснительного текста.
 * v7 (28.10, вливание в main f6f81424): файл объединён с main-версией. Всё,
 * что успел попасть в git, сохранено: чёрные кнопки/заголовки (4ea455d),
 * чип «ждёт ручной оценки» (isPendingManualReview), все хелперы и компоненты.
 * Аккордеон v2 (раскрытие по клику, 22d8393) заменён полностраничным отчётом
 * — это развитие той же просьбы врача. Серверная часть уже в main
 * (list_reports + rawAnswers + rateReport + миграция 015), поэтому таблица
 * «Ответы пациента» впервые оживает на реальных данных: raw приходит в строке
 * списка, вопросы шкалы — fetchScaleFromSupabase, сборка таблицы —
 * buildSummaryReport, чеклисты куба/часов — DrawingChecklist(Pentagon) с
 * пересчётом балла через rateReport и обновлением списка (refreshReports).
 * У движка шкал балл называется scoreResult.totalScore — учтено в scaleScoreOf.
 * ВАЖНО: API не отдаёт поля activity, поэтому «важная информация» на плашках
 * пациентов считается САМИМИ из реальных тренировок listCvTraces({patientId}):
 *   когда занимался, тренировок за неделю, всего, что тренировал последним (чипы).
 * Если когда-нибудь API вернёт link.activity — его поля берутся в приоритет.
 * Данные: ../lib/cortivoxApi (реальные), ../auth/AuthContext.
 * Классы cvx-* питаются вшитым блоком цветов ниже (лендинг и tailwind.config не трогаются).
 * v8: под-вкладка «Тренажер» в «Отчётах» карточки читает GET /api/trainers —
 * голосовой тренажёр «Речевая беглость» (счёт и зачтённые слова считает сервер).
 * Видео-упражнения (артикуляция) — отдельным блоком ниже, когда они есть.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Users, UserPlus, Archive, Activity, Search, AlertTriangle,
  CalendarDays, Mail, Loader2, LogOut, BrainCircuit, ArrowUpRight, ArrowLeft,
  GripVertical, ChevronRight, TrendingUp, TrendingDown, Calendar,
  Clock3, Play, CheckCircle2, X, AlertCircle, ClipboardList,
  Stethoscope, FileText, Plus, RotateCcw,
} from 'lucide-react';
import {
  getMyProfile, getLinks, lookupProfile, createLink,
  acceptLink, rejectLink, revokeLink,
  getCvIpr, putCvIpr, listCvTraces, getCvTrace, listScaleReports, rateReport,
  listTrainerReports,
} from '../lib/cortivoxApi';
import { useAuth } from '../auth/AuthContext';
import { SessionReportExpand } from './SessionReport';
import { exercisesCatalog } from '../exercises-data';
// Полностраничный отчёт по шкале собирается теми же материалами, что и
// «выписка» после «Провести»: лучевая диаграмма — recharts, вопросы шкалы —
// fetchScaleFromSupabase, таблица ответов — строитель buildSummaryReport,
// чеклисты ручных пунктов (куб и часы) — DrawingChecklist(Pentagon).
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { fetchScaleFromSupabase } from '../lib/fetchScale';
import { buildSummaryReport } from '../patient-records/buildSummaryReport';
// v5: полный разбор тестов скорости (Струп/SDMT) — тот же компонент, что
// показывает пациенту TestRunnerPage после прохождения (report.raw = scoreResult.stroop).
import SpeedTestReport from '../patient-records/SpeedTestReport';
import DrawingChecklist from '../scales-engine/itemTypes/drawing/DrawingChecklist';
import DrawingChecklistPentagon from '../scales-engine/itemTypes/drawing/DrawingChecklistPentagon';

// >>> CVX COLORS AUTO-BLOCK (не редактировать вручную) >>>
// String.raw — ОБЯЗАТЕЛЬНО: иначе JS в template literal съедает обратные слэши
// в селекторах (hover\:, \/40) и правила вариантов молча умирают.
// ЕДИНЫЙ блок для ВСЕХ страниц (кабинет врача, кабинет пациента, каталог,
// отчёт): какой бы файл ни загрузился первым — набор цветов одинаковый.
const CVX_COLORS_CSS = String.raw`
/* ==========================================================================
   cortivox-cvx.css — цвета кабинетов Cortivox (пациент и врач).
   Готово по демо UX «финал 25.10». НЕ трогает лендинг: это отдельные
   классы bg-cvx-*/text-cvx-*/..., которые нигде больше в проекте не встречаются.
   Подключение: в src/main.jsx после import "./index.css" добавить
     import "./cortivox-cvx.css";
   ========================================================================== */

.bg-cvx-accent{ background-color: #CFEFE0; }
.bg-cvx-accent\/10{ background-color: #CFEFE01A; }
.bg-cvx-background{ background-color: #F5FBF8; }
.bg-cvx-background\/95{ background-color: #F5FBF8F2; }
.bg-cvx-border{ background-color: #D8E2DE; }
.bg-cvx-card{ background-color: #FFFFFF; }
.bg-cvx-card\/60{ background-color: #FFFFFF99; }
.bg-cvx-muted{ background-color: #EDF4F1; }
.bg-cvx-muted-foreground\/50{ background-color: #576C6C80; }
.bg-cvx-popover{ background-color: #FFFFFF; }
.bg-cvx-primary{ background-color: #216858; }
.bg-cvx-primary-foreground\/20{ background-color: #F7FBF933; }
.bg-cvx-primary\/10{ background-color: #2168581A; }
.bg-cvx-primary\/25{ background-color: #21685840; }
.bg-cvx-primary\/40{ background-color: #21685866; }
.bg-cvx-primary\/80{ background-color: #216858CC; }
.bg-cvx-secondary{ background-color: #E8F2EE; }
.bg-cvx-secondary\/20{ background-color: #E8F2EE33; }
.bg-cvx-secondary\/30{ background-color: #E8F2EE4D; }
.bg-cvx-secondary\/40{ background-color: #E8F2EE66; }
.bg-cvx-secondary\/50{ background-color: #E8F2EE80; }
.bg-cvx-secondary\/60{ background-color: #E8F2EE99; }
.bg-cvx-sidebar{ background-color: #F0F9F6; }
.border-cvx-border{ border-color: #D8E2DE; }
.border-cvx-input{ border-color: #D8E2DE; }
.border-cvx-primary{ border-color: #216858; }
.divide-cvx-border{ border-color: #D8E2DE; }
.focus\:border-cvx-primary\/50:focus{ border-color: #21685880; }
.focus\:ring-cvx-ring:focus{ --tw-ring-color: #508E7E; }
.focus\:ring-cvx-ring\/20:focus{ --tw-ring-color: #508E7E33; }
.hover\:bg-cvx-accent:hover{ background-color: #CFEFE0; }
.hover\:bg-cvx-primary\/90:hover{ background-color: #216858E6; }
.hover\:bg-cvx-secondary:hover{ background-color: #E8F2EE; }
.hover\:bg-cvx-secondary\/50:hover{ background-color: #E8F2EE80; }
.hover\:bg-cvx-sidebar-accent:hover{ background-color: #CFEFE0; }
.hover\:border-cvx-destructive\/40:hover{ border-color: #DC262666; }
.hover\:border-cvx-primary\/40:hover{ border-color: #21685866; }
.hover\:border-cvx-primary\/50:hover{ border-color: #21685880; }
.hover\:text-cvx-destructive:hover{ color: #DC2626; }
.hover\:text-cvx-primary:hover{ color: #216858; }
.placeholder\:text-cvx-muted-foreground::placeholder{ color: #576C6C; }
.placeholder\:text-cvx-muted-foreground\/50::placeholder{ color: #576C6C80; }
.placeholder\:text-cvx-muted-foreground\/70::placeholder{ color: #576C6CB3; }
.ring-cvx-primary\/30{ --tw-ring-color: #2168584D; }
.stroke-cvx-primary{ stroke: #216858; }
.stroke-cvx-secondary{ stroke: #E8F2EE; }
.text-cvx-accent-foreground{ color: #094E3E; }
.text-cvx-destructive{ color: #DC2626; }
.text-cvx-foreground{ color: #182729; }
.text-cvx-muted-foreground{ color: #576C6C; }
.text-cvx-muted-foreground\/40{ color: #576C6C66; }
.text-cvx-muted-foreground\/60{ color: #576C6C99; }
.text-cvx-muted-foreground\/80{ color: #576C6CCC; }
.text-cvx-primary{ color: #216858; }
.text-cvx-primary-foreground{ color: #F7FBF9; }
.text-cvx-primary-foreground\/70{ color: #F7FBF9B3; }
.text-cvx-primary\/60{ color: #21685899; }
.text-cvx-secondary-foreground{ color: #18433A; }

/* Правки 26.10 — унификация с лендингом: чёрные заголовки, янтарное
   «Требуют внимания», логотип Cortivox.ai (как на лендинге) */
.bg-cvx-warn{ background-color: #FEF6E3; }
.bg-cvx-warn-card{ background-color: #FFFCF2; }
.border-cvx-warn{ border-color: #EFDFAE; }
.text-cvx-warn{ color: #B45309; }
.text-cvx-logo-ai{ color: #508E7E; }

/* Правки 26.10 v2 — как на лендинге: чёрный — для действий и активных
   состояний (кнопки, выбранные вкладки, логотип), зелёный — только акценты.
   ink = основной чёрный лендинга (shadcn neutral --primary, oklch 0.145 0 0) */
.bg-cvx-ink{ background-color: #0A0A0A; }
.bg-cvx-ink\/40{ background-color: #0A0A0A66; }
.hover\:bg-cvx-ink\/90:hover{ background-color: #0A0A0AE6; }
.text-cvx-ink{ color: #0A0A0A; }
.border-cvx-ink{ border-color: #0A0A0A; }
.hover\:text-cvx-ink:hover{ color: #0A0A0A; }

/* Единая кнопка «Назад» (везде одинаковая: отчёты, карточка пациента, чат) */
.cvx-back{ display:inline-flex; align-items:center; gap:.5rem; height:2.5rem; padding:0 1rem; border-radius:9999px; border:1px solid #D8E2DE; background:#FFFFFF; color:#0A0A0A; font-size:.875rem; font-weight:600; line-height:1; white-space:nowrap; flex:none; cursor:pointer; transition:background-color .15s ease, border-color .15s ease; }
.cvx-back:hover{ background:#E8F2EE; border-color:#C9DAD3; }
.cvx-back:disabled{ opacity:.5; }
.divide-cvx-border > :not([hidden]) ~ :not([hidden]) { border-color: #D8E2DE; }

/* Кастомные классы демо (не Tailwind) */
.cv-shadow { box-shadow: 0 1px 2px #10362f0a, 0 4px 16px #10362f0d; }
.cv-large { font-size: 1.125rem; }
.cv-large .cv-big-btn { border-radius: 1.25rem; height: 4rem; font-size: 1.1875rem; }
.cv-scroll::-webkit-scrollbar { width: 8px; }
.cv-scroll::-webkit-scrollbar-thumb { background: #d7e8e1; border-radius: 8px; }
`;
if (typeof document !== 'undefined') {
  // Версионная защита (26.10, v4): старый блок #cvx-colors (без версии = v0,
  // например из старого файла каталога) ЗАМЕНЯЕТСЯ этим блоком (v5). Раньше guard
  // «уже есть — пропустить» оставлял старые цвета навсегда, и чёрные кнопки/
  // логотип становились прозрачными, а белый текст — невидимым.
  const __cvxPrev = document.getElementById('cvx-colors');
  const __cvxPrevVer = __cvxPrev ? (Number(__cvxPrev.getAttribute('data-cvx-version') || '0') || 0) : 0;
  if (!__cvxPrev || __cvxPrevVer < 5) {
    if (__cvxPrev) __cvxPrev.parentNode.removeChild(__cvxPrev);
    const __cvxStyle = document.createElement('style');
    __cvxStyle.id = 'cvx-colors';
    __cvxStyle.setAttribute('data-cvx-version', '5');
    __cvxStyle.textContent = CVX_COLORS_CSS;
    document.head.appendChild(__cvxStyle);
  }
}
// <<< CVX COLORS AUTO-BLOCK <<<

// ─────────────────────────── Общие хелперы ───────────────────────────

const plural = (n, one, few, many) => {
  const a = n % 10, b = n % 100;
  if (a === 1 && b !== 11) return one;
  if (a >= 2 && a <= 4 && (b < 12 || b > 14)) return few;
  return many;
};
const initials = (v = '') => v.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
const fmtDateTime = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
};
const EX_TITLES = {
  schulte: 'Таблицы Шульте', sequence: 'Запомни порядок', pairs: 'Найди пару', words: 'Слова-ассоциации',
  clock: 'Установи время', math: 'Счёт в уме', figures: 'Лишняя фигура', trace: 'Обведи фигуру',
};
// Настоящие названия упражнений — из Каталога (../exercises-data); EX_TITLES — резерв.
const CAT_TITLES = Object.fromEntries(
  (Array.isArray(exercisesCatalog) ? exercisesCatalog : []).map((e) => [String(e.id), e.title]),
);
const titleOf = (slug) => CAT_TITLES[String(slug)] ?? EX_TITLES[String(slug)]
  ?? (String(slug).match(/^\d+$/) ? `Упражнение ${slug}` : String(slug));
const domainLabelOf = (slug) => 'Тренажёр';
const minutesOf = () => 10;
const SCALE_LABELS = { moca: 'MoCA', mmse: 'MMSE', sdmt: 'SDMT', sdmt_v2: 'SDMT-2', stroop: 'Струп', nihss: 'NIHSS', fab: 'FAB' };
const INACTIVE_DAYS = 7;
const fmtDateShort = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
};

// ─── Активность пациента из ЕГО реальных тренировок (listCvTraces) ───
// API поля activity не отдаёт — считаем сами: когда занимался, за неделю,
// всего, последние упражнения. Данные нет — lastActiveDays: null («ещё не занимался»).
const DAY_MS = 24 * 60 * 60 * 1000;
const activityFromTraces = (rows) => {
  const list = (Array.isArray(rows) ? rows : [])
    .map((r) => ({ ...r, _t: new Date(r.createdAt || r.created_at || '').getTime() }))
    .filter((r) => !Number.isNaN(r._t))
    .sort((a, b) => b._t - a._t); // свежие сверху
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  let lastActiveDays = null;
  if (list.length > 0) {
    const d = new Date(list[0]._t); d.setHours(0, 0, 0, 0);
    lastActiveDays = Math.max(0, Math.round((startOfToday - d.getTime()) / DAY_MS));
  }
  const weekAgo = Date.now() - 7 * DAY_MS;
  const weeklySessions = list.filter((r) => r._t >= weekAgo).length;
  const recentExercises = [];
  for (const r of list) {
    if (r.exerciseSlug && !recentExercises.some((x) => x.slug === r.exerciseSlug)) {
      recentExercises.push({ slug: r.exerciseSlug, date: fmtDateShort(r.createdAt || r.created_at) });
    }
    if (recentExercises.length >= 2) break;
  }
  return {
    lastActiveDays,
    weeklySessions,
    totalSessions: list.length,
    lastExerciseSlug: list.find((r) => r.exerciseSlug)?.exerciseSlug ?? null,
    recentExercises,
  };
};

// ─── Последние результаты ШКАЛ из настоящих отчётов (listScaleReports) ───
// Форма записи с сервера разбирается терпеливо: score / scoreResult.total /
// .totalScore (так поле называется у движка шкал) / .score / .sum;
// NIHSS-движок кладёт в scoreResult ГОЛОЕ ЧИСЛО (например 5) — тоже балл.
// дата — createdAt/created_at/scoredOn/scored_on.
const scaleScoreOf = (r) => {
  const s = r?.scoreResult;
  if (typeof s === 'number') return s;
  const direct = r?.score ?? s?.total ?? s?.totalScore ?? s?.score ?? s?.sum;
  if (direct != null) return direct;
  // v5: у Струпа/SDMT сервер кладёт отчёт во вложенный объект
  // (scoreResult.stroop / scoreResult.sdmt) — балл ищем внутри него.
  const inner = s && typeof s === 'object' ? (s.stroop ?? s.sdmt ?? null) : null;
  if (inner && typeof inner === 'object') {
    return inner.totalScore ?? inner.total ?? inner.score ?? inner.sum ?? null;
  }
  return null;
};
const scaleWhenOf = (r) => r.createdAt || r.created_at || r.scoredOn || r.scored_on || null;
const scaleWhenTs = (r) => { const t = new Date(scaleWhenOf(r) || 0).getTime(); return Number.isNaN(t) ? 0 : t; };
const scaleKeyOf = (r) => String(r.scale || r.scaleId || r.scale_id || '');

const scaleLatestFromReports = (rows) => {
  const list = Array.isArray(rows) ? rows.filter((r) => r && scaleKeyOf(r)) : [];
  const byScale = new Map();
  for (const r of list) {
    if (!byScale.has(scaleKeyOf(r))) byScale.set(scaleKeyOf(r), []);
    byScale.get(scaleKeyOf(r)).push(r);
  }
  return [...byScale.entries()].map(([scale, arr]) => {
    const sorted = [...arr].sort((a, b) => scaleWhenTs(b) - scaleWhenTs(a));
    const last = sorted[0];
    const prev = sorted[1];
    const score = scaleScoreOf(last);
    const prevScore = prev == null ? null : scaleScoreOf(prev);
    const delta = typeof score === 'number' && typeof prevScore === 'number' ? score - prevScore : null;
    return {
      scale,
      label: SCALE_LABELS[scale] || scale,
      score,
      delta,
      date: scaleWhenOf(last) ? fmtDateShort(scaleWhenOf(last)) : null,
      _ts: scaleWhenTs(last),
    };
  }).sort((a, b) => b._ts - a._ts)
    .map(({ _ts, ...r }) => r); // свежие шкалы сверху, служебное поле убрать
};

const Avatar = ({ name, size = 'md' }) => {
  const sizes = { sm: 'size-8 text-xs', md: 'size-11 text-sm', lg: 'size-14 text-lg', xl: 'size-20 text-2xl' };
  return (
    <span aria-hidden className={`inline-flex shrink-0 items-center justify-center rounded-2xl bg-cvx-accent font-bold text-cvx-accent-foreground ${sizes[size]}`}>
      {initials(name)}
    </span>
  );
};

function StatCard({ icon: Icon, label, value, tone = 'default', hint }) {
  // «Требуют внимания» — тёплая янтарная плашка (просьба врача 26.10);
  // остальные значения — чёрные (как на лендинге, зелёного меньше).
  const warn = tone === 'warn';
  return (
    <div className={`cv-shadow rounded-2xl border p-5 ${warn ? 'bg-cvx-warn border-cvx-warn' : tone === 'good' ? 'bg-[oklch(0.965_0.03_155)] border-[oklch(0.88_0.05_155)]' : 'bg-cvx-card border-cvx-border'}`}>
      <div className={`flex items-center gap-2 text-sm ${warn ? 'text-cvx-warn' : 'text-cvx-muted-foreground'}`}>
        <Icon className="size-4" /> {label}
      </div>
      <p className={`mt-2 text-3xl font-bold tracking-tight ${warn ? 'text-cvx-warn' : 'text-cvx-foreground'}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-cvx-muted-foreground">{hint}</p>}
    </div>
  );
}

function AdherenceBar({ value }) {
  const color = value >= 75 ? 'bg-[oklch(0.6_0.13_150)]' : value >= 50 ? 'bg-[oklch(0.75_0.13_85)]' : 'bg-[oklch(0.64_0.16_25)]';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-cvx-secondary">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${value}%` }} />
      </div>
      <span className="text-xs font-semibold text-cvx-muted-foreground">{value}%</span>
    </div>
  );
}

function WeekChart({ data, className = '' }) {
  const days = Array.isArray(data) && data.length === 7
    ? data
    : ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((l) => ({ label: l, minutes: 0 }));
  const max = Math.max(...days.map((d) => d.minutes), 1);
  return (
    <div className={`flex items-end justify-between gap-2 ${className}`} role="img" aria-label="Активность за неделю в минутах">
      {days.map((d) => (
        <div key={d.label} className="flex flex-1 flex-col items-center gap-1.5">
          <div className="flex h-16 w-full items-end justify-center">
            <div
              className={`w-full max-w-[1.5rem] rounded-t-[1rem] transition-all ${d.minutes === 0 ? 'bg-cvx-secondary' : 'bg-cvx-primary/80'}`}
              style={{ height: `${Math.max((d.minutes / max) * 100, 6)}%` }}
              title={`${d.minutes} мин`}
            />
          </div>
          <span className="text-[11px] leading-none text-cvx-muted-foreground">{d.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────── Шкалы ───────────────────────────

const SCALES = [
  { id: 'moca', label: 'MoCA', full: 'Монреальская шкала оценки когнитивных функций', group: 'cognitive', desc: 'Скрининг лёгких когнитивных нарушений: память, речь, счёт, ориентация. Учитывается образование.', minutes: 12 },
  { id: 'mmse', label: 'MMSE', full: 'Краткая шкала оценки психического статуса', group: 'cognitive', desc: 'Базовый скрининг когнитивного статуса: ориентация во времени и месте, запоминание, счёт.', minutes: 10 },
  { id: 'sdmt', label: 'SDMT', full: 'Тест символов и цифр (оригинальная версия)', group: 'attention', desc: 'Скорость обработки информации и зрительное сканирование за 90 секунд.', minutes: 5 },
  { id: 'sdmt_v2', label: 'SDMT-2', full: 'Тест символов и цифр (пилотная версия)', group: 'attention', desc: 'Пилотный вариант SDMT с автопроверкой ответов на экране.', minutes: 5 },
  { id: 'stroop', label: 'Струп', full: 'Тест Струпа', group: 'attention', desc: 'Тормозящий контроль и селективное внимание: цвет слова конфликтует со значением.', minutes: 6 },
  { id: 'nihss', label: 'NIHSS', full: 'Шкала оценки тяжести инсульта NIH', group: 'neuro', desc: 'Количественная оценка неврологического дефицита: сознание, зрение, моторика, речь.', minutes: 15 },
];
const SCALE_GROUPS = [
  { key: 'cognitive', title: 'Когнитивные скрининги', hint: 'Общая оценка когнитивных функций: память, внимание, речь, ориентация' },
  { key: 'attention', title: 'Внимание и скорость обработки', hint: 'Скорость зрительного поиска, переключаемость, устойчивость внимания' },
  { key: 'neuro', title: 'Неврологический статус', hint: 'Неврологический дефицит по стандартной шкале инсульта' },
];

// ─────────────────────────── Карточка пациента ───────────────────────────

const CARD_TABS = [['overview', 'Обзор'], ['scales', 'Шкалы'], ['ipr', 'ИПР'], ['reports', 'Отчёты']];

function TrendBars({ trend }) {
  if (!Array.isArray(trend) || trend.length < 2 || !trend.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
  const max = Math.max(...trend, 1);
  return (
    <div className="flex h-12 items-end gap-1" aria-label="Динамика за последние тренировки">
      {trend.map((v, i) => (
        <div
          key={i}
          className={`w-2.5 rounded-t ${i === trend.length - 1 ? 'bg-cvx-primary' : 'bg-cvx-primary/25'}`}
          style={{ height: `${Math.max((v / max) * 100, 8)}%` }}
          title={`${v}`}
        />
      ))}
    </div>
  );
}

function SessionReportCard({ r, sessions }) {
  return (
    <article className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-bold text-cvx-foreground">{titleOf(r.exerciseSlug || r.exerciseSlug)}</h4>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-cvx-muted-foreground">
            <Calendar className="size-3.5" /> {fmtDateTime(r.createdAt || r.createdAt)}
            {typeof r.frames === 'number' && (
              <span className="rounded-[0.875rem] bg-cvx-secondary px-2 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                запись: {r.frames} кадров{r.truncated ? ' (обрезана)' : ''}
              </span>
            )}
          </p>
        </div>
      </div>
      {/* Разбор сессии — 5 согласованных секций: заключение, «было → стало»,
          кривая с порогами и подсказками, таблица динамики, детализация замечаний.
          Полная запись грузится только по нажатию (несколько МБ). */}
      <div className="mt-3">
        <SessionReportExpand
          row={r}
          sessions={Array.isArray(sessions) ? sessions : [r]}
          exerciseTitle={titleOf(r.exerciseSlug)}
        />
      </div>
    </article>
  );
}

function PatientReportsTab({ rows }) {
  if (!Array.isArray(rows))
    return (
      <p className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Загрузка отчётов…
      </p>
    );
  if (rows.length === 0)
    return (
      <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-14 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
          <ClipboardList className="size-6" />
        </span>
        <p className="font-semibold text-cvx-primary">Тренировок пока нет</p>
        <p className="max-w-sm text-sm text-cvx-muted-foreground">Отчёт появится здесь после первого занятия пациента с тренажёрами.</p>
      </div>
    );
  return (
    <div className="flex flex-col gap-3">
      {rows.map((r) => <SessionReportCard key={r.id} r={r} sessions={rows} />)}
    </div>
  );
}

// ─── Отчёты ПО ШКАЛАМ: Summary + все оценки (данные GET /api/reports) ───

// Заключение из scoreResult (если сервер его прислал) — терпеливо,
// серверные версии называют поле по-разному.
const scaleInterpretationOf = (r) => {
  const s = r?.scoreResult;
  for (const k of ['interpretation', 'interpretationText', 'summary']) {
    if (typeof s?.[k] === 'string' && s[k].trim()) return s[k];
  }
  return null;
};

// Домены из scoreResult (если присланы): [{domain, score, max}]
const scaleSubScoresOf = (r) => {
  const arr = r?.scoreResult?.subScores;
  return Array.isArray(arr)
    ? arr.filter((s) => s && typeof s.domain === 'string' && s.score != null)
    : null;
};

// Порог нормы из scoreResult (сервер считает его с учётом возраста — ageNote
// рядом объясняет); если сервер назовёт поле иначе — терпеливый фолбэк.
const scaleCutoffOf = (r) => {
  const s = r?.scoreResult;
  const c = s?.ageAdjustedCutoff ?? s?.cutoff ?? s?.normCutoff;
  return typeof c === 'number' ? c : null;
};

// Оценка одного пункта таблицы ответов — текстом, как на «выписке».
const ScaleAnswerMark = ({ item }) => {
  if (item.needsManualScore) return <span className="text-cvx-muted-foreground">ждёт ручной оценки</span>;
  if (item.points != null) return <span>{item.points} {plural(item.points, 'балл', 'балла', 'баллов')}</span>;
  if (item.isCorrect === true) return <span>1 балл</span>;
  if (item.isCorrect === false) return <span>0 баллов</span>;
  return <span className="text-cvx-muted-foreground">—</span>;
};

// ПОЛНОСТРАНИЧНЫЙ отчёт по одной оценке (v4): ...
function ScaleReportPage({ r, onBack, patientName, patientProfile, refreshReports }) {
  const s = r?.scoreResult && typeof r.scoreResult === 'object' ? r.scoreResult : null;
  const score = scaleScoreOf(r);
  const rawScore = typeof s?.rawScore === 'number' ? s.rawScore : null;
  const cutoff = scaleCutoffOf(r);
  const subScores = scaleSubScoresOf(r);
  const maxOf = (sub) => (sub?.max != null ? sub.max : sub?.total != null ? sub.total : null);
  const mem = s?.memoryDetail && typeof s.memoryDetail === 'object' ? s.memoryDetail : null;
  const vig = s?.vigilanceDetail && typeof s.vigilanceDetail === 'object' ? s.vigilanceDetail : null;
  const mis = typeof s?.mis === 'number' ? s.mis : null;
  const vlom = typeof s?.vlomRatio === 'number' ? s.vlomRatio : null;
  const vlomValid = s?.isVlomValid !== false && vlom != null;
  const scenarios = Array.isArray(s?.clinicalScenarios) && s.clinicalScenarios.length > 0 ? s.clinicalScenarios : null;
  const diffs = Array.isArray(s?.differentialDiagnosis) && s.differentialDiagnosis.length > 0 ? s.differentialDiagnosis : null;
  const interpretation = scaleInterpretationOf(r);
  const label = SCALE_LABELS[scaleKeyOf(r)] || scaleKeyOf(r) || 'Шкала';
  // ── v5: Струп/SDMT — полный разбор теста скорости ──
  // Сервер считает эти тесты сам и кладёт готовый отчёт в scoreResult.stroop
  // (scoreResult.sdmt). Показываем его тем же компонентом, что и пациенту
  // после прохождения (TestRunnerPage): report.raw = вложенный отчёт.
  const sKey = scaleKeyOf(r);
  const isSpeedScale = sKey === 'stroop' || sKey === 'sdmt' || sKey === 'sdmt_v2';
  const speedRaw = s && typeof s === 'object' ? (s.stroop ?? s.sdmt ?? s.sdmt_v2 ?? null) : null;
  const when = scaleWhenOf(r);
  const verdict = score != null && cutoff != null ? (score >= cutoff ? 'В пределах порога нормы' : 'Ниже порога нормы') : null;

  // ── Таблица «Ответы пациента» ──
  // Строится из СЫРЫХ ответов (rawAnswers), которые сервер хранит, но пока НЕ
  // присылает в списке GET /api/reports (задание Клоду приложено к пакету).
  // Как только в строке появятся rawAnswers/raw_answers — подтягиваем вопросы
  // шкалы и собираем таблицу тем же строителем, что и на «выписке».
  const raw = r?.rawAnswers && typeof r.rawAnswers === 'object' && Object.keys(r.rawAnswers).length > 0 ? r.rawAnswers
    : r?.raw_answers && typeof r.raw_answers === 'object' && Object.keys(r.raw_answers).length > 0 ? r.raw_answers : null;
  const [scaleData, setScaleData] = useState(null);
  const [detailedAnswers, setDetailedAnswers] = useState(null);
  const [manualScores, setManualScores] = useState({});
  const [ratingState, setRatingState] = useState('idle'); // idle | saving | done | error
  const [ratingError, setRatingError] = useState('');

  useEffect(() => {
    if (!raw) return undefined;
    let alive = true;
    setScaleData(null); setDetailedAnswers(null);
    fetchScaleFromSupabase(scaleKeyOf(r))
      .then((data) => { if (alive) setScaleData(data || null); })
      .catch(() => { if (alive) setScaleData(null); });
    return () => { alive = false; };
  }, [raw, r]);

  useEffect(() => {
    if (!raw || !scaleData || !Array.isArray(scaleData.questions)) return;
    const rep = buildSummaryReport(
      String(scaleKeyOf(r)).toUpperCase(),
      s ?? score ?? 0,
      raw,
      scaleData,
      patientProfile || { name: patientName || '', age: null, educationYears: 12 },
      r?.instructionRepeats || {},
    );
    setDetailedAnswers(Array.isArray(rep?.detailedAnswers) ? rep.detailedAnswers.filter(Boolean) : []);
  }, [raw, scaleData, r, s, score, patientProfile, patientName]);

  const manualItemIds = scaleData && Array.isArray(scaleData.questions)
    ? scaleData.questions.filter((q) => q.type === 'drawing' || q.isManualAction).map((q) => q.id)
    : [];

  // Чеклист куба/часов заполнен → rateReport (как на «выписке»), список
  // отчётов обновится, плашка «Предварительные данные» уйдёт.
  const handleChecklistSubmit = async (itemId, checklist) => {
    const next = { ...manualScores, [itemId]: checklist };
    setManualScores(next);
    if (r?.id == null || ratingState === 'saving') return;
    setRatingState('saving'); setRatingError('');
    try {
      await rateReport(r.id, { manualScores: next, manualItemIds });
      setRatingState('done');
      if (refreshReports) refreshReports();
      setTimeout(() => onBack(), 1200); // показать «сохранено» и вернуться к списку
    } catch (err) {
      setRatingState('error');
      setRatingError(err?.message || 'Не удалось сохранить ручную оценку — попробуйте ещё раз');
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onBack(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onBack]);

  const misTone = mis == null ? 'text-cvx-foreground' : mis < 5 ? 'text-[oklch(0.55_0.15_25)]' : mis >= 8 ? 'text-[oklch(0.5_0.12_150)]' : 'text-[oklch(0.55_0.15_45)]';
  const misBar = mis == null ? 'bg-cvx-secondary' : mis < 5 ? 'bg-[oklch(0.55_0.15_25)]' : mis >= 8 ? 'bg-[oklch(0.5_0.12_150)]' : 'bg-[oklch(0.7_0.15_60)]';

  return (
    <div className="cv-scroll fixed inset-0 z-50 overflow-y-auto bg-cvx-background">
      <div className="mx-auto flex max-w-5xl flex-col gap-5 p-4 sm:p-8">
        <button onClick={onBack} className="cvx-back self-start">
          <ArrowLeft className="size-4" /> Назад к отчётам
        </button>

        {r.isPendingManualReview && (
          <div className="flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-blue-800">
            <AlertTriangle className="size-6 shrink-0 text-blue-500" />
            <div>
              <p className="text-sm font-bold">Предварительные данные</p>
              <p className="text-xs">Часть пунктов шкалы ещё не проверена вручную. Балл и заключение станут окончательными после ручной оценки.</p>
            </div>
          </div>
        )}
        {typeof s?.ageNote === 'string' && s.ageNote.trim() && (
          <div className="flex items-start gap-3 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-yellow-800">
            <AlertCircle className="size-6 shrink-0 text-yellow-500" />
            <p className="text-xs font-medium">{s.ageNote}</p>
          </div>
        )}

        <section className="cv-shadow flex flex-col items-start justify-between gap-5 rounded-3xl border border-cvx-border bg-cvx-card p-6 md:flex-row md:items-center">
          <div className="flex min-w-0 flex-col gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-cvx-foreground">Отчёт: {label}</h1>
            <p className="text-sm text-cvx-muted-foreground">
              {patientName ? <>Пациент: {patientName}{when ? ' · ' : ''}</> : null}
              {when ? <>Дата: {fmtDateTime(when)}</> : null}
            </p>
            {subScores && subScores.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {subScores.map((sub, i) => (
                  <span key={i} className="rounded-full bg-cvx-secondary px-2 py-1 text-[11px] font-medium text-cvx-primary">
                    {sub.domain}: {sub.score}/{maxOf(sub) ?? '—'}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-8">
            <div className="text-center">
              <p className="text-xs uppercase tracking-wider text-cvx-muted-foreground">Балл</p>
              <p className="text-5xl font-bold text-cvx-primary">{score ?? '—'}</p>
              {rawScore != null && score != null && rawScore !== score && (
                <p className="mt-1 whitespace-nowrap text-[10px] text-cvx-muted-foreground">сырой {rawScore} · итог {score}</p>
              )}
            </div>
            <div className="text-center md:text-left">
              <p className="text-xs uppercase tracking-wider text-cvx-muted-foreground">Интерпретация</p>
              <p className={`text-lg font-semibold ${verdict ? (score < cutoff ? 'text-[oklch(0.55_0.15_25)]' : 'text-[oklch(0.5_0.12_150)]') : 'text-cvx-secondary-foreground'}`}>
                {verdict || '—'}
              </p>
              {cutoff != null && <p className="text-[11px] text-cvx-muted-foreground">порог нормы: {cutoff}</p>}
            </div>
          </div>
        </section>

        {(vig || mem || mis != null || vlom != null || scenarios || diffs || interpretation) && (
          <section className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-6">
            <h2 className="mb-4 text-lg font-bold text-cvx-foreground">Клиническая интерпретация (AI)</h2>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="flex flex-col gap-4">
                {vig && (vig.commissions > 0 || vig.omissions > 0) && (
                  <div className="rounded-2xl border border-cvx-border bg-cvx-background p-4">
                    <p className="mb-2 text-sm font-medium text-cvx-muted-foreground">Бдительность (хлопки на «А») — экспериментально</p>
                    <div className="mb-2 grid grid-cols-2 gap-2 text-center">
                      <div className="rounded-xl bg-cvx-secondary py-2">
                        <p className="text-lg font-bold text-cvx-foreground">{vig.commissions ?? 0}</p>
                        <p className="text-[10px] text-cvx-muted-foreground">лишних хлопков</p>
                      </div>
                      <div className="rounded-xl bg-cvx-secondary py-2">
                        <p className="text-lg font-bold text-cvx-foreground">{vig.omissions ?? 0}</p>
                        <p className="text-[10px] text-cvx-muted-foreground">пропусков</p>
                      </div>
                    </div>
                    {typeof vig.pattern === 'string' && vig.pattern && (
                      <p className="text-xs text-cvx-muted-foreground">
                        паттерн ошибок: {vig.pattern === 'mixed' ? 'смешанный' : vig.pattern === 'omission' ? 'в основном пропуски' : vig.pattern === 'commission' ? 'в основном лишние хлопки' : vig.pattern}
                      </p>
                    )}
                  </div>
                )}
                {mem && (
                  <div className="rounded-2xl border border-cvx-border bg-cvx-background p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm font-medium text-cvx-muted-foreground">Память (свободное воспроизведение)</p>
                      <p className="text-xl font-bold text-cvx-foreground">{mem.freeRecallCount ?? 0} / 5</p>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="rounded-xl bg-cvx-secondary py-2">
                        <p className="text-lg font-bold text-cvx-foreground">{mem.freeRecallCount ?? 0}</p>
                        <p className="text-[10px] text-cvx-muted-foreground">свободно</p>
                      </div>
                      <div className="rounded-xl bg-cvx-secondary py-2">
                        <p className="text-lg font-bold text-cvx-foreground">{mem.hintCount ?? 0}</p>
                        <p className="text-[10px] text-cvx-muted-foreground">с подсказкой</p>
                      </div>
                      <div className="rounded-xl bg-cvx-secondary py-2">
                        <p className="text-lg font-bold text-cvx-foreground">{mem.choiceCount ?? 0}</p>
                        <p className="text-[10px] text-cvx-muted-foreground">с выбором</p>
                      </div>
                      <div className="rounded-xl bg-cvx-secondary py-2">
                        <p className="text-lg font-bold text-cvx-foreground">{mem.failCount ?? 0}</p>
                        <p className="text-[10px] text-cvx-muted-foreground">не вспомнил</p>
                      </div>
                    </div>
                    <p className="mt-2 text-[10px] italic text-cvx-muted-foreground/60">
                      Официальный балл шкалы учитывает только «свободно».
                    </p>
                  </div>
                )}
                {mis != null && (
                  <div className="rounded-2xl border border-cvx-border bg-cvx-background p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm font-medium text-cvx-muted-foreground">Индекс памяти (MIS) — маркер БА</p>
                      <p className={`text-xl font-bold ${misTone}`}>{mis} / 15</p>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-cvx-secondary">
                      <div className={`h-2.5 rounded-full transition-all duration-500 ${misBar}`} style={{ width: `${Math.max(0, Math.min(100, (mis / 15) * 100))}%` }} />
                    </div>
                    <p className="mt-2 text-xs text-cvx-muted-foreground">{typeof s?.misPrognosis === 'string' && s.misPrognosis ? s.misPrognosis : 'Данные по прогнозу отсутствуют'}</p>
                  </div>
                )}
                {vlom != null && (
                  <div className="rounded-2xl border border-cvx-border bg-cvx-background p-4">
                    <div className="mb-1 flex items-center justify-between">
                      <p className="text-sm font-medium text-cvx-muted-foreground">VLOM-индекс (Язык / Память + Ориентация)</p>
                      <p className="text-xl font-bold text-cvx-foreground">{vlomValid ? vlom : '—'}</p>
                    </div>
                    <p className="text-xs text-cvx-muted-foreground">
                      {vlomValid
                        ? (vlom > 0.8 ? 'Высокий показатель (маркер Болезни Альцгеймера)' : vlom < 0.5 ? 'Низкий показатель (маркер bvFTD)' : 'Промежуточный профиль (равномерное снижение)')
                        : 'Недостаточно данных (Память и Ориентация = 0)'}
                    </p>
                    <p className="mt-1 text-[10px] italic text-cvx-muted-foreground/60">*Пороги эмпирические, адаптация для скрининга</p>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-4">
                {interpretation && (
                  <div className="rounded-2xl bg-cvx-muted px-4 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-cvx-muted-foreground">Заключение</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-cvx-foreground">{interpretation}</p>
                  </div>
                )}
                {scenarios && (
                  <div className={`rounded-2xl border-2 p-4 ${scenarios[0]?.includes('Сценарий А') ? 'border-yellow-400 bg-yellow-50 text-yellow-800' : scenarios[0]?.includes('Сценарий Б') ? 'border-red-400 bg-red-50 text-red-800' : 'border-orange-400 bg-orange-50 text-orange-800'}`}>
                    <p className="text-sm font-semibold leading-relaxed">{scenarios.join(' ')}</p>
                  </div>
                )}
                {diffs && (
                  <div className="rounded-2xl border border-cvx-border bg-cvx-background p-4">
                    <p className="mb-1 text-sm font-semibold text-cvx-foreground">Гипотезы по профилю</p>
                    <p className="mb-3 text-[11px] text-cvx-muted-foreground">Черновик для врача — не диагноз.</p>
                    {diffs.map((d, idx) => (
                      <div key={idx} className="mb-3 border-b border-cvx-border pb-3 last:mb-0 last:border-b-0 last:pb-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-bold text-cvx-primary">Данные поддерживают: {d?.diagnosis}</p>
                          {d?.confidence === 'low' && <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold text-red-700">Низкая уверенность</span>}
                          {d?.confidence === 'medium' && <span className="rounded-full bg-yellow-100 px-1.5 py-0.5 text-[10px] font-bold text-yellow-700">Средняя уверенность</span>}
                        </div>
                        {d?.reason && <p className="mt-1 text-xs leading-relaxed text-cvx-muted-foreground">{d.reason}</p>}
                        {d?.recommendation && (
                          <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-cvx-secondary p-2">
                            <ClipboardList className="mt-0.5 size-3.5 shrink-0 text-cvx-primary" />
                            <p className="text-xs font-medium text-cvx-secondary-foreground">{d.recommendation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="mt-3 border-t border-cvx-border pt-3 text-[10px] italic leading-relaxed text-cvx-muted-foreground/60">
                      Гипотеза построена по профилю одного скрининга и не является диагнозом. Смешанный нейродегенеративный или
                      сосудистый процесс не может быть исключён по единственному обследованию — требуется подтверждение анамнезом,
                      нейровизуализацией и полной нейропсихологической оценкой.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {subScores && subScores.length > 0 && (
          <section className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-6">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              <div>
                <h2 className="mb-4 text-lg font-bold text-cvx-foreground">Профиль когнитивных функций (%)</h2>
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={subScores.map((sub) => ({
                      domain: String(sub.domain || '').split(' ')[0],
                      fullMark: 100,
                      value: maxOf(sub) ? Math.max(0, Math.min(100, Math.round((sub.score / maxOf(sub)) * 100))) : 0,
                    }))}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="domain" tick={{ fill: '#6b7280', fontSize: 14, fontWeight: 'bold' }} />
                      <PolarRadiusAxis domain={[0, 100]} tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={true} />
                      <Radar name="Score" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div>
                <h2 className="mb-4 text-lg font-bold text-cvx-foreground">Детализация по доменам</h2>
                <div className="flex flex-col gap-3">
                  {subScores.map((sub, idx) => {
                    const max = maxOf(sub);
                    const pct = max ? Math.max(0, Math.min(100, Math.round((sub.score / max) * 100))) : 0;
                    return (
                      <div key={idx} className="flex items-center justify-between gap-3 rounded-xl border border-cvx-border bg-cvx-background p-3">
                        <span className="min-w-0 flex-1 text-sm font-medium text-cvx-foreground">{sub.domain}</span>
                        <div className="flex shrink-0 items-center gap-2">
                          <div className="h-2.5 w-24 overflow-hidden rounded-full bg-cvx-secondary">
                            <div className="h-2.5 rounded-full bg-cvx-primary" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-12 text-right text-sm font-semibold text-cvx-muted-foreground">{max != null ? `${sub.score}/${max}` : sub.score}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        {isSpeedScale && speedRaw && typeof speedRaw === 'object' && (
          <SpeedTestReport
            report={{
              kind: 'speed',
              server: true,
              saved: true,
              scaleId: sKey,
              scaleName: sKey === 'stroop' ? 'Тест Струпа' : (SCALE_LABELS[sKey] || 'Тест скорости'),
              algorithm: r?.algorithmVersion ? `сервер · ${r.algorithmVersion}` : 'сервер',
              raw: speedRaw,
            }}
            onExit={onBack}
          />
        )}

        {raw != null && detailedAnswers && detailedAnswers.length > 0 && (
          <section className="cv-shadow rounded-3xl border border-cvx-border bg-cvx-card p-6">
            <h2 className="mb-1 text-lg font-bold text-cvx-foreground">Ответы пациента</h2>
            <p className="mb-4 text-xs text-cvx-muted-foreground">Сравнение ответов с эталоном по каждому пункту шкалы.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b-2 border-cvx-border text-cvx-muted-foreground">
                    <th className="w-12 px-2 py-3">№</th>
                    <th className="px-2 py-3">Вопрос</th>
                    <th className="min-w-[150px] px-2 py-3">Ответ пациента</th>
                    <th className="min-w-[100px] px-2 py-3">Эталон</th>
                    <th className="w-36 px-2 py-3 text-center">Оценка</th>
                  </tr>
                </thead>
                <tbody>
                  {detailedAnswers.map((item) => {
                    const tone = item.needsManualScore ? 'bg-blue-50 border-blue-200'
                      : item.isCorrect === null ? 'bg-yellow-50 border-yellow-200'
                      : item.isCorrect === true ? 'bg-green-50 border-green-200'
                      : (item.points ?? 0) > 0 ? 'bg-yellow-50 border-yellow-200'
                      : 'bg-red-50 border-red-200';
                    return (
                      <tr key={item.id} className={`border-b border-cvx-border ${tone}`}>
                        <td className="px-2 py-2 font-bold text-cvx-muted-foreground">{item.id}</td>
                        <td className="px-2 py-2 text-xs text-cvx-foreground">
                          {item.text}
                          {item.repeats > 0 && (
                            <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[10px] text-cvx-muted-foreground" title={`Инструкция повторена ${item.repeats} раз(а)`}>
                              <RotateCcw className="size-2.5" />×{item.repeats}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-xs text-cvx-foreground">
                          {item.image
                            ? <img src={item.image} alt="Рисунок пациента" className="h-24 w-24 rounded border border-cvx-border bg-white object-contain" />
                            : item.patientAnswer}
                        </td>
                        <td className="px-2 py-2 text-xs text-cvx-muted-foreground">{Array.isArray(item.correctAnswer) ? item.correctAnswer.join(', ') : item.correctAnswer}</td>
                        <td className="px-2 py-2 text-center text-xs font-semibold text-cvx-foreground">
                          {item.needsManualScore && r.isPendingManualReview ? (
                            item.id === '11a'
                              ? <DrawingChecklistPentagon itemId={item.id} onSubmit={handleChecklistSubmit} />
                              : <DrawingChecklist itemId={item.id} onSubmit={handleChecklistSubmit} />
                          ) : (
                            <ScaleAnswerMark item={item} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {r.isPendingManualReview && manualItemIds.length > 0 && (
              <p className="mt-3 text-xs leading-relaxed text-cvx-muted-foreground">
                Заполните чеклисты по кубу и часам прямо в таблице — балл пересчитается автоматически,
                и плашка «Предварительные данные» уйдёт.
              </p>
            )}
            {ratingState !== 'idle' && (
              <p className={`mt-2 text-xs font-semibold ${ratingState === 'error' ? 'text-cvx-destructive' : 'text-[oklch(0.5_0.12_150)]'}`}>
                {ratingState === 'saving' ? 'Сохраняем ручную оценку…' : ratingState === 'done' ? 'Ручная оценка сохранена — отчёт обновлён.' : ratingError}
              </p>
            )}
          </section>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-cvx-muted-foreground">
          {r.algorithmVersion && <span>алгоритм: {r.algorithmVersion}</span>}
          {r.id != null && <span>отчёт № {String(r.id).slice(0, 8)}</span>}
          {r.isPendingManualReview && <span className="font-semibold text-cvx-warn">итог предварительный</span>}
        </div>

        <div className="flex justify-center pb-6">
          <button onClick={onBack} className="cvx-back">
            <ArrowLeft className="size-4" /> Назад к отчётам
          </button>
        </div>
      </div>
    </div>
  );
}

// Одна оценка в «Все оценки»: строка-кнопка, клик открывает отчёт НА ВСЮ
// СТРАНИЦУ (просьба врача 26.10: «отчёты должны раскрываться на всю страницу»).
function ScaleReportRow({ r, onOpen }) {
  const key = scaleKeyOf(r);
  const score = scaleScoreOf(r);
  const when = scaleWhenOf(r);
  return (
    <button
      type="button"
      onClick={() => onOpen(r)}
      className="flex w-full flex-wrap items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-card px-4 py-3 text-left transition-colors hover:border-cvx-primary/40"
    >
      <ChevronRight className="size-4 shrink-0 text-cvx-muted-foreground" />
      <span className="rounded-[1rem] bg-cvx-secondary px-2 py-1 text-xs font-bold text-cvx-secondary-foreground">{SCALE_LABELS[key] || key || 'Шкала'}</span>
      <span className="text-lg font-bold text-cvx-foreground">{score ?? '—'}</span>
      {r.isPendingManualReview && (
        <span className="rounded-full bg-cvx-warn px-2.5 py-0.5 text-xs font-semibold text-cvx-warn">ждёт ручной оценки</span>
      )}
      <span className="ml-auto flex items-center gap-2 text-xs text-cvx-muted-foreground">
        {when ? fmtDateTime(when) : ''}
        <span className="font-semibold text-cvx-primary">открыть отчёт</span>
      </span>
    </button>
  );
}

function ScaleReportsTab({ scaleRep, onGoScales, patientName, patientProfile, refreshReports }) {
  const status = scaleRep?.status || 'loading';
  const [openReport, setOpenReport] = useState(null); // оценка, чей отчёт открыт на всю страницу
  if (status === 'loading')
    return (
      <p className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Загрузка отчётов по шкалам…
      </p>
    );
  if (status === 'unavailable')
    return (
      <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-12 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
          <ClipboardList className="size-6" />
        </span>
        <p className="font-semibold text-cvx-primary">Отчёты по шкалам появятся после обновления сервера</p>
        <p className="max-w-md text-sm leading-relaxed text-cvx-muted-foreground">
          Сервер пока не отдаёт список оценок по шкалам (нужен один новый эндпоинт GET /api/reports).
          Файл-промпт «ПРОМПТ-Клоду-отчёты-по-шкалам.md» приложен к пакету — после обновления данные появятся здесь сами.
        </p>
      </div>
    );
  const items = Array.isArray(scaleRep?.items) ? [...scaleRep.items].sort((a, b) => scaleWhenTs(b) - scaleWhenTs(a)) : [];
  if (items.length === 0)
    return (
      <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-12 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
          <ClipboardList className="size-6" />
        </span>
        <p className="font-semibold text-cvx-ink">Оценок по шкалам пока нет</p>
        <p className="max-w-sm text-sm text-cvx-muted-foreground">Проведите шкалу во вкладке «Шкалы» — результат сохранится и появится здесь.</p>
        <button onClick={onGoScales} className="inline-flex items-center gap-1.5 rounded-full bg-cvx-ink px-5 py-2.5 text-sm font-semibold text-cvx-primary-foreground transition-colors hover:bg-cvx-ink/90">
          Перейти к шкалам
        </button>
      </div>
    );
  const latest = scaleLatestFromReports(items);
  return (
    <div className="flex flex-col gap-4">
      {/* Summary — обзор по шкалам сверху (просьба врача: «может быть сначала Summary?») */}
      <section className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <h3 className="font-bold text-cvx-foreground">Обзор по шкалам</h3>
        <ul className="mt-3 flex flex-col gap-2.5">
          {latest.map((r) => (
            <li key={r.scale} className="flex flex-wrap items-center gap-3 rounded-[1.25rem] border border-cvx-border px-3.5 py-3">
              <span className="rounded-[1rem] bg-cvx-primary/10 px-2 py-1 text-xs font-bold text-cvx-primary">{r.label}</span>
              <span className="text-xl font-bold text-cvx-foreground">{r.score ?? '—'}</span>
              {typeof r.delta === 'number' && (
                <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${r.delta >= 0 ? 'text-[oklch(0.5_0.12_150)]' : 'text-[oklch(0.55_0.15_25)]'}`}>
                  {r.delta >= 0 ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
                  {Math.abs(r.delta)} к прошлому
                </span>
              )}
              <span className="ml-auto text-xs text-cvx-muted-foreground">последний: {r.date || '—'}</span>
            </li>
          ))}
        </ul>
      </section>
      <div>
        <h3 className="font-bold text-cvx-foreground">Все оценки</h3>
        <p className="text-xs text-cvx-muted-foreground">Нажмите на оценку — отчёт откроется на всю страницу.</p>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((r, i) => <ScaleReportRow key={r.id || i} r={r} onOpen={setOpenReport} />)}
      </div>
      {openReport && (
        <ScaleReportPage r={openReport} patientName={patientName} patientProfile={patientProfile} refreshReports={refreshReports} onBack={() => setOpenReport(null)} />
      )}
    </div>
  );
}

// ─── «Тренажер»: голосовой тренажёр «Речевая беглость» (GET /api/trainers) ───
// Счёт и зачтённые слова считает СЕРВЕР (правила MoCA) — здесь только показ.
// Раньше эта под-вкладка ошибочно показывала cv_traces (видео/артикуляция).
//
// В той же таблице trainer_reports живут результаты «Зрительного восприятия»
// (stepId 'v01'..'v20' ПО КАРТОЧКЕ — каждая показывается 2 занятия подряд,
// algorithm_version visual-web-1): карточка различает их по stepId. Поля
// читаем и с плоского объекта, и из scoreResult — работает при любом
// маппинге в cortivoxApi.

const VISUAL_STEP_RE = /^v\d+$/; // 'v01'..'v20' — «Зрительное восприятие»
const RETELL_STEP_RE = /^r\d+$/; // 'r01'..'r04' — «Пересказ близко к тексту»
const SCHULTE_STEP_RE = /^s\d+$/; // 's01'..'s20' — «Таблицы Шульте»

const fmtElapsedCard = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

function TrainerReportCard({ r }) {
  const sr = r?.scoreResult || {};
  const isVisual = VISUAL_STEP_RE.test(String(r?.stepId || ''));
  const count = Number(r?.count ?? sr.count ?? 0);
  const counted = Array.isArray(r?.counted) ? r.counted
    : (Array.isArray(sr.counted) ? sr.counted : []);

  if (isVisual) {
    const total = Number(r?.total ?? sr.total ?? 0);
    const matched = Array.isArray(r?.matched) ? r.matched
      : (Array.isArray(sr.matched) ? sr.matched : counted);
    const missed = Array.isArray(r?.missed) ? r.missed
      : (Array.isArray(sr.missed) ? sr.missed : []);
    const errors = Array.isArray(r?.errors) ? r.errors
      : (Array.isArray(sr.errors) ? sr.errors : []);
    const elapsedMs = r?.elapsedMs ?? sr.elapsedMs;
    const thinkingMs = r?.thinkingMs ?? sr.thinkingMs; // «на раздумье»
    // Номер в stepId теперь — КАРТОЧКА (1..20), она показывается 2 занятия:
    // карточка 5 → дни 9–10.
    const cardNum = parseInt(String(r.stepId).slice(1), 10) || 0;
    return (
      <article className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-bold text-cvx-foreground">Зрительное восприятие</h4>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
              <Calendar className="size-3.5" /> {fmtDateShort(r.scoredOn)}
              <span className="rounded-[0.875rem] bg-cvx-secondary px-2 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                карточка {cardNum} · дни {cardNum * 2 - 1}–{cardNum * 2}
              </span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">
              {count}<span className="text-lg font-semibold text-cvx-muted-foreground">/{total}</span>
            </p>
            <p className="mt-1 text-xs text-cvx-muted-foreground">
              названо верно
              {elapsedMs != null ? ` · общее ${fmtElapsedCard(elapsedMs)}` : ''}
              {thinkingMs != null ? ` · раздумье ${fmtElapsedCard(thinkingMs)}` : ''}
            </p>
          </div>
        </div>
        {matched.length > 0 && (
          <p className="mt-3 rounded-xl bg-cvx-muted p-3.5 text-sm leading-relaxed text-cvx-foreground">
            {matched.join(', ')}
          </p>
        )}
        {missed.length > 0 && (
          <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">
            Не названо: {missed.join(', ')}
          </p>
        )}
        {errors.length > 0 && (
          <p className="mt-1 text-xs leading-relaxed text-cvx-muted-foreground">
            Лишние слова ({errors.length}): {errors.join(', ')}
          </p>
        )}
      </article>
    );
  }

  if (RETELL_STEP_RE.test(String(r?.stepId || ''))) {
    // «Пересказ близко к тексту»: percent по ядерным вехам, блоки, флаги цепочек.
    const percent = Number(sr.percent ?? 0);
    const coreClosed = Number(sr.coreClosed ?? 0);
    const coreTotal = Number(sr.coreTotal ?? 0);
    const detailClosed = Number(sr.detailClosed ?? 0);
    const detailTotal = Number(sr.detailTotal ?? 0);
    const blocks = Array.isArray(sr.blocks) ? sr.blocks : [];
    const flags = Array.isArray(sr.flags) ? sr.flags : [];
    const partialNums = Array.isArray(sr.partialNums) ? sr.partialNums : [];
    const storyTitle = r?.config?.title || sr.title || '';
    const elapsedMs = r?.elapsedMs ?? sr.elapsedMs;
    return (
      <article className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-bold text-cvx-foreground">Пересказ близко к тексту</h4>
            <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
              <Calendar className="size-3.5" /> {fmtDateShort(r.scoredOn)}
              {storyTitle && (
                <span className="rounded-[0.875rem] bg-cvx-secondary px-2 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                  «{storyTitle}»
                </span>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">
              {percent}<span className="text-lg font-semibold text-cvx-muted-foreground">%</span>
            </p>
            <p className="mt-1 text-xs text-cvx-muted-foreground">
              ядро {coreClosed}/{coreTotal} вех
              {detailTotal > 0 ? ` · детали ${detailClosed}/${detailTotal}` : ''}
              {elapsedMs != null ? ` · ${fmtElapsedCard(elapsedMs)}` : ''}
              {` · подсказок ${Number(sr.hintsUsed) || 0}`}
            </p>
          </div>
        </div>
        {blocks.length > 0 && (
          <p className="mt-3 text-xs leading-relaxed text-cvx-muted-foreground">
            Блоки: {blocks.map((b) => `${b.name} ${b.coreClosed}/${b.coreTotal}`).join(' · ')}
          </p>
        )}
        {partialNums.length > 0 && (
          <p className="mt-2 text-xs leading-relaxed text-cvx-muted-foreground">
            Частично (сверить по стенограмме): вехи {partialNums.join(', ')}
          </p>
        )}
        {flags.map((f) => (
          <p key={f.type} className="mt-2 text-xs font-semibold leading-relaxed text-amber-700">
            Флаг «{f.label}». {f.message}
          </p>
        ))}
      </article>
    );
  }

if (SCHULTE_STEP_RE.test(String(r?.stepId || ''))) {
  const tableNum = parseInt(String(r.stepId).slice(1), 10) || 0;
  const errors = Number(r?.errors ?? sr.errors ?? 0);
  const elapsedMs = r?.elapsedMs ?? sr.elapsedMs;
  const done = r?.completed ?? sr.completed;
  return (
    <article className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-bold text-cvx-foreground">Таблицы Шульте</h4>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
            <Calendar className="size-3.5" /> {fmtDateShort(r.scoredOn)}
            <span className="rounded-[0.875rem] bg-cvx-secondary px-2 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
              таблица {tableNum} · дни {tableNum * 2 - 1}–{tableNum * 2}
            </span>
            {done === false && ' · не завершено'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">
            {fmtElapsedCard(elapsedMs)}
          </p>
          <p className="mt-1 text-xs text-cvx-muted-foreground">
            время · найдено {count}/49 · ошибок {errors}
          </p>
        </div>
      </div>
    </article>
  );
}

  return (
    <article className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-bold text-cvx-foreground">Речевая беглость</h4>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-cvx-muted-foreground">
            <Calendar className="size-3.5" /> {fmtDateShort(r.scoredOn)}
            {r.stepId && (
              <span className="rounded-[0.875rem] bg-cvx-secondary px-2 py-0.5 text-xs font-semibold text-cvx-secondary-foreground">
                блок {r.stepId}
              </span>
            )}
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold leading-none tabular-nums text-cvx-primary">{count}</p>
          <p className="mt-1 text-xs text-cvx-muted-foreground">{plural(count, 'слово', 'слова', 'слов')} засчитано</p>
        </div>
      </div>
      {counted.length > 0 && (
        <p className="mt-3 rounded-xl bg-cvx-muted p-3.5 text-sm leading-relaxed text-cvx-foreground">
          {counted.join(', ')}
        </p>
      )}
    </article>
  );
}

function TrainerReportsTab({ patientId, videoRows }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!patientId) { setItems([]); return undefined; }
    let alive = true;
    setItems(null); setErr('');
    // Тот же эндпоинт, куда пишет тренажёр: GET /api/trainers?patientId=&limit=
    listTrainerReports({ patientId, limit: 200 })
      .then((res) => {
        if (!alive) return;
        const list = (Array.isArray(res) ? res : res?.items || [])
          .slice()
          .sort((a, b) => String(b?.scoredOn || '').localeCompare(String(a?.scoredOn || '')));
        setItems(list);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.message || 'Не удалось получить отчёты тренажёра');
        setItems([]);
      });
    return () => { alive = false; };
  }, [patientId]);

  if (items == null)
    return (
      <p className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Загрузка отчётов…
      </p>
    );

  if (items.length === 0)
    return (
      <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-14 text-center">
        <span className="flex size-14 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
          <FileText className="size-6" />
        </span>
        <p className="font-semibold text-cvx-primary">{err ? 'Отчёты тренажёра недоступны' : 'Прохождений пока нет'}</p>
        <p className="max-w-sm text-sm text-cvx-muted-foreground">
          {err ? err : 'Отчёт появится здесь после того, как пациент пройдёт «Речевую беглость» в своём кабинете.'}
        </p>
      </div>
    );

  const weekly = items.filter((r) => Date.now() - scaleWhenTs({ createdAt: r.scoredOn }) < 7 * DAY_MS).length;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-semibold text-cvx-muted-foreground">
        всего прохождений: {items.length}{weekly > 0 ? ` · за 7 дней: ${weekly}` : ''}
      </p>
      {items.map((r) => <TrainerReportCard key={r.id || `${r.stepId}-${r.scoredOn}`} r={r} />)}
      {/* Видео-упражнения (артикуляция) — другой тип тренажёров; показываем, только если есть */}
      {Array.isArray(videoRows) && videoRows.length > 0 && (
        <>
          <h3 className="mt-4 text-sm font-bold uppercase tracking-wider text-cvx-muted-foreground">Видео-упражнения (артикуляция)</h3>
          <PatientReportsTab rows={videoRows} />
        </>
      )}
    </div>
  );
}

// ─── Вкладка «Отчёты» карточки: под-вкладки Шкалы / Тренажер ───

function PatientReportsSection({ rows, scaleRep, onGoScales, patientName, patientProfile, refreshReports, patientId }) {
  const [sub, setSub] = useState('scales');
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-2xl border border-cvx-border bg-cvx-card p-1.5" role="tablist" aria-label="Тип отчётов">
          {[['scales', 'Шкалы'], ['trainer', 'Тренажер']].map(([key, label]) => (
            <button
              key={key}
              role="tab" aria-selected={sub === key} onClick={() => setSub(key)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${sub === key ? 'bg-cvx-ink text-cvx-primary-foreground shadow' : 'text-cvx-secondary-foreground hover:bg-cvx-secondary'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {sub === 'scales'
        ? <ScaleReportsTab scaleRep={scaleRep} onGoScales={onGoScales} patientName={patientName} patientProfile={patientProfile} refreshReports={refreshReports} />
        : <TrainerReportsTab patientId={patientId} videoRows={rows} />}
    </div>
  );
}

function ScalesTab({ patient, lastResults }) {
  const navigate = useNavigate();
  const [started, setStarted] = useState(null);
  const results = Array.isArray(lastResults) ? lastResults : [];
  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm leading-relaxed text-cvx-muted-foreground">
        Выберите шкалу — она откроется в режиме проведения с этим пациентом. Результат сохранится в карточке автоматически.
      </p>
      {SCALE_GROUPS.map((g) => {
        const list = SCALES.filter((s) => s.group === g.key);
        return (
          <section key={g.key} className="flex flex-col gap-3">
            <div>
              <h3 className="font-bold text-cvx-foreground">{g.title}</h3>
              <p className="text-sm text-cvx-muted-foreground">{g.hint}</p>
            </div>
            <div className="grid gap-3 lg:grid-cols-2">
              {list.map((s) => {
                const last = results.find((r) => r.scale === s.id);
                return (
                  <article key={s.id} className="cv-shadow flex flex-col gap-3 rounded-2xl border border-cvx-border bg-cvx-card p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="rounded-[1rem] bg-cvx-primary/10 px-2.5 py-1 text-sm font-bold text-cvx-primary">{s.label}</span>
                          {last && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-cvx-muted-foreground">
                              последний: {last.score}
                              {typeof last.delta === 'number' && (
                                <span className={`inline-flex items-center ${last.delta >= 0 ? 'text-[oklch(0.5_0.12_150)]' : 'text-[oklch(0.55_0.15_25)]'}`}>
                                  {last.delta >= 0 ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
                                  {Math.abs(last.delta)}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                        <p className="mt-2 text-sm font-semibold text-cvx-secondary-foreground">{s.full}</p>
                        <p className="mt-1 text-sm leading-relaxed text-cvx-muted-foreground">{s.desc}</p>
                      </div>
                    </div>
                    <div className="mt-auto flex items-center justify-between gap-3 border-t border-cvx-border pt-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-cvx-muted-foreground">
                        <Clock3 className="size-3.5" /> ≈{s.minutes} мин{last && <> · {last.date}</>}
                      </span>
                      <button
                        onClick={() => { setStarted(s.id); navigate(`/test/${s.id}`, { state: { patientProfile: patient } }); }}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${started === s.id ? 'bg-[oklch(0.93_0.05_150)] text-[oklch(0.4_0.1_150)]' : 'bg-cvx-ink text-cvx-primary-foreground hover:bg-cvx-ink/90'}`}
                      >
                        {started === s.id ? <CheckCircle2 className="size-4" /> : <Play className="size-4" />}
                        {started === s.id ? 'Сессия начата' : 'Провести'}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function IprTab({ patientId, slugs, onCommit, saving, savedNote }) {
  const dragFrom = useRef(null);
  const [over, setOver] = useState(null);
  const [adding, setAdding] = useState(false); // панель «Добавить упражнение» с галочками
  if (!Array.isArray(slugs))
    return (
      <p className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Загрузка плана…
      </p>
    );
  // Группа каталога по модулям (Речь / Когнитивные навыки / Мимика / Моторика) — порядок как в каталоге
  const groups = [];
  for (const ex of Array.isArray(exercisesCatalog) ? exercisesCatalog : []) {
    const d = ex?.domain || 'Другое';
    let g = groups.find((x) => x.name === d);
    if (!g) { g = { name: d, items: [] }; groups.push(g); }
    g.items.push(ex);
  }
  const toggle = (slug) => {
    const next = slugs.includes(slug) ? slugs.filter((s) => s !== slug) : [...slugs, slug];
    onCommit(next); // сохраняется сразу, как и галочки в Каталоге
  };
  const AddPanel = adding && (
    <div className="flex flex-col gap-4 rounded-2xl border border-cvx-border bg-cvx-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-cvx-foreground">Отметьте упражнения — они войдут в план пациента</p>
        <button onClick={() => setAdding(false)} className="rounded-full border border-cvx-border px-4 py-2 text-sm font-semibold text-cvx-primary transition-colors hover:bg-cvx-secondary">
          Свернуть
        </button>
      </div>
      {groups.map((g) => (
        <div key={g.name}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-cvx-muted-foreground">{g.name}</p>
          <div className="flex flex-col gap-1.5">
            {g.items.map((ex) => {
              const slug = String(ex.id);
              const checked = slugs.includes(slug);
              return (
                <label key={slug} className={`flex cursor-pointer items-center gap-3 rounded-[1.25rem] border px-3.5 py-3 transition-colors ${checked ? 'border-cvx-primary bg-cvx-primary/5' : 'border-cvx-border hover:border-cvx-primary/40'}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(slug)} className="size-5 accent-[#216858]" />
                  <span className="min-w-0 flex-1 truncate font-semibold text-cvx-foreground">{ex.title}</span>
                  <span className="text-xs text-cvx-muted-foreground">≈{minutesOf(slug)} мин</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-xs text-cvx-muted-foreground">Изменения сохраняются автоматически. Порядок выполнения — перетаскиванием в списке выше.</p>
    </div>
  );
  if (slugs.length === 0)
    return (
      <div className="flex flex-col gap-4">
        {AddPanel}
        <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-14 text-center">
          <span className="flex size-14 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
            <ClipboardList className="size-6" />
          </span>
          <p className="font-semibold text-cvx-ink">План реабилитации пуст</p>
          <p className="max-w-sm text-sm text-cvx-muted-foreground">
            Отметьте упражнения — они появятся здесь, и пациент будет выполнять их по порядку.
          </p>
          <button
            onClick={() => setAdding(true)}
            style={{ backgroundColor: '#0A0A0A' }} className="inline-flex items-center gap-1.5 rounded-full bg-cvx-ink px-4 py-2.5 text-xs font-semibold text-cvx-primary-foreground transition-transform hover:-translate-y-0.5"
          >
            <Plus className="size-3.5" /> Добавить упражнение
          </button>
        </div>
      </div>
    );
  const drop = (to) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    setOver(null);
    if (from == null || from === to) return;
    const next = [...slugs];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onCommit(next);
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-cvx-muted-foreground">Порядок выполнения — меняйте перетаскиванием. Пациент видит план в этой очередности.</p>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-cvx-muted-foreground">
            {saving ? 'Сохраняем…' : savedNote ? 'Сохранено' : `${slugs.length} ${plural(slugs.length, 'упражнение', 'упражнения', 'упражнений')}`}
          </span>
          <button
            onClick={() => setAdding((v) => !v)}
            style={{ backgroundColor: '#0A0A0A' }} className="inline-flex items-center gap-1.5 rounded-full bg-cvx-ink px-4 py-2.5 text-xs font-semibold text-cvx-primary-foreground transition-transform hover:-translate-y-0.5"
          >
            <Plus className="size-3.5" /> {adding ? 'Закрыть список' : 'Добавить упражнение'}
          </button>
        </div>
      </div>
      {AddPanel}
      <ol className="flex flex-col gap-2">
        {slugs.map((slug, i) => (
          <li
            key={slug}
            draggable
            onDragStart={() => { dragFrom.current = i; }}
            onDragOver={(e) => { e.preventDefault(); setOver(i); }}
            onDrop={() => drop(i)}
            onDragEnd={() => { dragFrom.current = null; setOver(null); }}
            className={`flex cursor-grab items-center gap-3 rounded-2xl border bg-cvx-card px-4 py-3.5 transition-colors active:cursor-grabbing ${over === i ? 'border-cvx-primary' : 'border-cvx-border hover:border-cvx-primary/40'}`}
          >
            <GripVertical className="size-4 shrink-0 text-cvx-muted-foreground/60" />
            <span className="flex size-7 shrink-0 items-center justify-center rounded-[1rem] bg-cvx-secondary text-sm font-bold text-cvx-secondary-foreground">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-cvx-foreground">{titleOf(slug)}</p>
              <p className="truncate text-xs text-cvx-muted-foreground">{domainLabelOf(slug)} · ≈{minutesOf(slug)} мин</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function OverviewGrid({ activity, iprSlugs, reportRows, onGoTab, latestScale }) {
  // Приоритет — реальные отчёты по шкалам (GET /api/reports); fallback — activity.latestResults из API
  const latest = (Array.isArray(latestScale) && latestScale.length)
    ? latestScale
    : (Array.isArray(activity?.latestResults) ? activity.latestResults : []);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-cvx-foreground">Последние результаты шкал</h3>
          <button onClick={() => onGoTab('scales')} className="text-sm font-semibold text-cvx-primary hover:underline">Все шкалы</button>
        </div>
        {latest.length === 0 ? (
          <p className="mt-3 text-sm text-cvx-muted-foreground">Оценок ещё нет — проведите первую шкалу во вкладке «Шкалы».</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-2.5">
            {latest.map((r) => {
              const s = SCALES.find((x) => x.id === r.scale);
              return (
                <li key={r.scale} className="flex items-center gap-3 rounded-[1.25rem] border border-cvx-border px-3.5 py-3">
                  <span className="rounded-[1rem] bg-cvx-primary/10 px-2 py-1 text-xs font-bold text-cvx-primary">{s?.label || r.label || r.scale}</span>
                  <span className="text-lg font-bold text-cvx-primary">{r.score}</span>
                  {typeof r.delta === 'number' && (
                    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${r.delta >= 0 ? 'text-[oklch(0.5_0.12_150)]' : 'text-[oklch(0.55_0.15_25)]'}`}>
                      {r.delta >= 0 ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
                      {Math.abs(r.delta)} к прошлому
                    </span>
                  )}
                  <span className="ml-auto text-xs text-cvx-muted-foreground">{r.date}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-cvx-foreground">Активность за неделю</h3>
          {activity?.streak != null && (
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-cvx-primary">
              <CalendarDays className="size-4 text-[oklch(0.7_0.15_60)]" /> {activity.streak} подряд
            </span>
          )}
        </div>
        <WeekChart className="mt-4" data={activity?.week} />
        <p className="mt-3 text-sm text-cvx-muted-foreground">
          {activity?.lastActiveDays != null
            ? activity.lastActiveDays === 0 ? 'занимался сегодня' : `не занимался ${activity.lastActiveDays} ${plural(activity.lastActiveDays, 'день', 'дня', 'дней')}`
            : 'ещё не занимался'}
          {activity?.weeklySessions != null ? ` · за неделю: ${activity.weeklySessions}` : ''}
          {activity?.totalSessions != null ? ` · всего: ${activity.totalSessions}` : ''}
          {activity?.adherence != null ? ` · приверженность ${activity.adherence}%` : ''}
        </p>
      </section>

      <section className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-cvx-foreground">ИПР</h3>
          <button onClick={() => onGoTab('ipr')} className="text-sm font-semibold text-cvx-primary hover:underline">Открыть план</button>
        </div>
        {Array.isArray(iprSlugs) ? (
          iprSlugs.length === 0 ? (
            <p className="mt-3 text-sm text-cvx-muted-foreground">План пуст — добавьте упражнения кнопкой во вкладке «ИПР».</p>
          ) : (
            <ol className="mt-3 flex flex-col gap-1.5 text-sm">
              {iprSlugs.slice(0, 3).map((slug, i) => (
                <li key={slug} className="flex items-center gap-2.5 rounded-[1.25rem] bg-cvx-secondary/50 px-3 py-2.5 text-cvx-primary">
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-[0.875rem] bg-cvx-card text-xs font-bold text-cvx-secondary-foreground">{i + 1}</span>
                  {titleOf(slug)}
                </li>
              ))}
              {iprSlugs.length > 3 && (
                <li className="px-1 text-xs text-cvx-muted-foreground">и ещё {iprSlugs.length - 3} {plural(iprSlugs.length - 3, 'упражнение', 'упражнения', 'упражнений')}…</li>
              )}
            </ol>
          )
        ) : (
          <p className="mt-3 text-sm text-cvx-muted-foreground">Загрузка…</p>
        )}
      </section>

      <section className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-cvx-foreground">Последние тренировки</h3>
          <button onClick={() => onGoTab('reports')} className="text-sm font-semibold text-cvx-primary hover:underline">Все отчёты</button>
        </div>
        {Array.isArray(reportRows) ? (
          reportRows.length === 0 ? (
            <p className="mt-3 text-sm text-cvx-muted-foreground">Пока нет завершённых тренировок.</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5">
              {reportRows.slice(0, 3).map((r) => (
                <li key={r.id} className="flex items-center gap-2.5 rounded-[1.25rem] border border-cvx-border px-3 py-2.5 text-sm">
                  <Play className="size-4 shrink-0 text-cvx-primary" />
                  <span className="truncate font-medium text-cvx-secondary-foreground">{titleOf(r.exerciseSlug)}</span>
                  <span className="ml-auto shrink-0 text-xs text-cvx-muted-foreground">
                    {fmtDateTime(r.createdAt).split(',')[0]}{typeof r.score === 'number' ? ` · ${r.score} б.` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )
        ) : (
          <p className="mt-3 text-sm text-cvx-muted-foreground">Загрузка…</p>
        )}
      </section>
    </div>
  );
}

export function PatientCardView({ patient, onBack }) {
  const location = useLocation();
  // Врач может открыть карточку сразу на нужной вкладке — кнопками «ИПР»/«Отчёты» на плашке
  const [tab, setTab] = useState(location.state?.initialTab || 'overview');
  const [slugs, setSlugs] = useState(null);
  const [rows, setRows] = useState(null);
  const [scaleRep, setScaleRep] = useState(null); // {status:'loading'|'ready'|'unavailable', items}
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const activity = patient.activity || {};
  const inactive = (activity.lastActiveDays ?? 0) >= INACTIVE_DAYS;

  useEffect(() => {
    let alive = true;
    setSlugs(null); setRows(null); setScaleRep(null);
    getCvIpr(patient.id)
      .then((d) => { if (alive) setSlugs((Array.isArray(d) ? d : d?.items || []).map((it) => (typeof it === 'string' ? it : it.exerciseSlug))); })
      .catch(() => { if (alive) setSlugs([]); });
    listCvTraces({ patientId: patient.id })
      .then((d) => { if (alive) setRows(d || []); })
      .catch(() => { if (alive) setRows([]); });
    // Отчёты ПО ШКАЛАМ: если сервер ещё не отдаёт GET /api/reports — честный пустой стейт
    listScaleReports({ patientId: patient.id })
      .then((d) => { if (alive) setScaleRep({ status: 'ready', items: Array.isArray(d) ? d : d?.items || [] }); })
      .catch(() => { if (alive) setScaleRep({ status: 'unavailable', items: [] }); });
    return () => { alive = false; };
  }, [patient.id]);

  const commitIpr = async (next) => {
    setSlugs(next); setSaving(true);
    try {
      await putCvIpr(next, patient.id);
      setSavedNote(true);
      setTimeout(() => setSavedNote(false), 1500);
    } catch { /* noop */ }
    finally { setSaving(false); }
  };
  // Перечитать список шкальных отчётов (после сохранения ручной оценки куба/часов
  // в полностраничном отчёте — балл и плашка «ждёт ручной оценки» обновятся).
  const refreshScaleReports = useCallback(() => {
    listScaleReports({ patientId: patient.id })
      .then((d) => setScaleRep({ status: 'ready', items: Array.isArray(d) ? d : d?.items || [] }))
      .catch(() => setScaleRep({ status: 'unavailable', items: [] }));
  }, [patient.id]);
  const archive = async () => {
    setArchiving(true);
    try { if (patient.linkId) await revokeLink(patient.linkId); onBack(); }
    catch { onBack(); }
    finally { setArchiving(false); }
  };

  return (
    <div className="flex flex-col gap-6 p-5 lg:p-8">
      <div className="flex flex-col gap-5">
        {/* Единая кнопка «Назад» — такая же, как в отчётах и чате */}
        <button onClick={onBack} className="cvx-back">
          <ArrowLeft className="size-4" /> Все пациенты
        </button>
        <div className={`cv-shadow flex flex-wrap items-center gap-5 rounded-3xl border bg-cvx-card p-6 ${inactive ? 'border-cvx-warn bg-cvx-warn-card' : 'border-cvx-border'}`}>
          <Avatar name={patient.name || patient.email || ''} size="xl" />
          <div className="min-w-56 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight text-cvx-foreground">{patient.name || patient.email}</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[oklch(0.93_0.05_150)] px-2.5 py-1 text-xs font-semibold text-[oklch(0.4_0.1_150)]">
                <span className="size-1.5 rounded-full bg-[oklch(0.6_0.15_150)]" /> Активен
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-cvx-muted-foreground">
              {patient.age != null && <span>{patient.age} {plural(patient.age, 'год', 'года', 'лет')}</span>}
              {patient.educationYears != null && <span>образование: {patient.educationYears} {plural(patient.educationYears, 'год', 'года', 'лет')}</span>}
              {patient.email && <span>{patient.email}</span>}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1 text-sm font-medium">
              {(activity.lastActiveDays != null || activity.totalSessions != null) && (
                <span className={`inline-flex items-center gap-1.5 ${inactive ? 'text-[oklch(0.55_0.15_45)]' : 'text-cvx-muted-foreground'}`}>
                  <CalendarDays className="size-3.5" />
                  {activity.lastActiveDays != null
                    ? (activity.lastActiveDays === 0 ? 'занимался сегодня' : activity.lastActiveDays === 1 ? 'занимался вчера' : `не занимался ${activity.lastActiveDays} ${plural(activity.lastActiveDays, 'день', 'дня', 'дней')}`)
                    : 'ещё не занимался'}
                </span>
              )}
              {activity.weeklySessions != null && (
                <span className="inline-flex items-center gap-1.5 text-cvx-primary">
                  {activity.weeklySessions} {plural(activity.weeklySessions, 'тренировка', 'тренировки', 'тренировок')} / 7 дней
                </span>
              )}
              {activity.totalSessions != null && (
                <span className="inline-flex items-center gap-1.5 text-cvx-muted-foreground">всего: {activity.totalSessions}</span>
              )}
              {activity.streak > 0 && (
                <span className="inline-flex items-center gap-1.5 text-cvx-primary">
                  <CalendarDays className="size-3.5 text-[oklch(0.7_0.15_60)]" /> {activity.streak} {plural(activity.streak, 'день', 'дня', 'дней')} подряд
                </span>
              )}
            </div>
          </div>
          <button
            onClick={archive} disabled={archiving}
            className="inline-flex items-center gap-2 rounded-full border border-cvx-border px-4 py-2.5 text-sm font-semibold text-cvx-muted-foreground transition-colors hover:border-cvx-destructive/40 hover:text-cvx-destructive disabled:opacity-60"
          >
            <Archive className="size-4" /> Архивировать
          </button>
        </div>
        <div className="flex gap-1.5 overflow-x-auto rounded-2xl border border-cvx-border bg-cvx-card p-1.5" role="tablist" aria-label="Разделы карточки пациента">
          {CARD_TABS.map(([key, label]) => (
            <button
              key={key}
              role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
              className={`shrink-0 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${tab === key ? 'bg-cvx-ink text-cvx-primary-foreground shadow' : 'text-cvx-secondary-foreground hover:bg-cvx-secondary'}`}
            >
              {label}{key === 'reports' && Array.isArray(rows) ? ` · ${rows.length}` : ''}
            </button>
          ))}
        </div>
      </div>
      <div role="tabpanel">
        {tab === 'overview' && <OverviewGrid activity={activity} iprSlugs={slugs} reportRows={rows} onGoTab={setTab} latestScale={scaleLatestFromReports(scaleRep?.status === 'ready' ? scaleRep.items : null)} />}
        {tab === 'scales' && <ScalesTab patient={patient} lastResults={scaleLatestFromReports(scaleRep?.status === 'ready' ? scaleRep.items : null)} />}
        {tab === 'ipr' && <IprTab patientId={patient.id} slugs={slugs} onCommit={commitIpr} saving={saving} savedNote={savedNote} />}
        {tab === 'reports' && <PatientReportsSection rows={rows} scaleRep={scaleRep} onGoScales={() => setTab('scales')} patientName={patient.name || patient.email} patientProfile={{ id: patient.id, name: patient.name || patient.email, age: patient.age, educationYears: patient.educationYears }} refreshReports={refreshScaleReports} patientId={patient.id} />}
      </div>
    </div>
  );
}

// ─────────────────────────── Карточка пациента в списке ───────────────────────────

function PatientCardItem({ link, onOpen }) {
  const c = link.counterpart || {};
  const a = link.activity || {};
  const name = c.full_name || c.email || link.patient_id;
  const inactive = (a.lastActiveDays ?? 0) >= INACTIVE_DAYS;
  const hasAge = c.age != null;
  const hasEdu = c.education_years != null || c.educationYears != null;
  const edu = c.education_years ?? c.educationYears;
  const latest = Array.isArray(a.latestResults) ? a.latestResults : [];
  const lastActiveText = (d) =>
    d == null ? 'ещё не занимался' : d === 0 ? 'занимался сегодня' : d === 1 ? 'занимался вчера' : `не занимался ${d} ${plural(d, 'день', 'дня', 'дней')}`;

  return (
    <article className={`cv-shadow group flex flex-col gap-4 rounded-3xl border bg-cvx-card p-5 transition-all hover:-translate-y-0.5 hover:border-cvx-primary/40 ${inactive ? 'border-cvx-warn bg-cvx-warn-card' : 'border-cvx-border'}`}>
      <div className="flex items-start gap-3.5">
        <Avatar name={name} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <button onClick={() => onOpen({})} className="text-left text-lg font-bold leading-tight text-cvx-foreground hover:underline">
              {name}
            </button>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${link.status === 'active' ? 'bg-[oklch(0.93_0.05_150)] text-[oklch(0.4_0.1_150)]' : 'bg-cvx-muted text-cvx-muted-foreground'}`}>
              <span className={`size-1.5 rounded-full ${link.status === 'active' ? 'bg-[oklch(0.6_0.15_150)]' : 'bg-cvx-muted-foreground/50'}`} />
              {link.status === 'active' ? 'Активен' : 'В архиве'}
            </span>
          </div>
          {(hasAge || hasEdu) && (
            <p className="mt-1 text-sm text-cvx-muted-foreground">
              {hasAge && <>{c.age} {plural(c.age, 'год', 'года', 'лет')}{hasEdu ? ' · ' : ''}</>}
              {hasEdu && <>образование {edu} {plural(edu, 'год', 'года', 'лет')}</>}
            </p>
          )}
          {/* Когда занимался: если тренировок нет — честно «ещё не занимался» (янтарным, если давно) */}
          {(a.lastActiveDays != null || a.weeklySessions != null) && (
            <p className={`mt-1.5 flex items-center gap-1.5 text-sm font-medium ${inactive ? 'text-cvx-warn' : 'text-cvx-muted-foreground'}`}>
              {inactive ? <AlertTriangle className="size-3.5" /> : <CalendarDays className="size-3.5" />}
              {lastActiveText(a.lastActiveDays)}
            </p>
          )}
          {/* Всего тренировок — реальное число из отчётов (важная информация на плашке) */}
          {a.totalSessions > 0 && (
            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-cvx-muted-foreground">
              <Activity className="size-3.5 text-cvx-primary" /> всего тренировок: {a.totalSessions}
            </p>
          )}
        </div>
      </div>
      {(a.streak != null || a.weeklySessions != null || a.adherence != null) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {a.streak != null && (
            <span className="inline-flex items-center gap-1.5 font-semibold text-cvx-foreground">
              <CalendarDays className={`size-4 ${a.streak > 0 ? 'text-[oklch(0.7_0.15_60)]' : 'text-cvx-muted-foreground/40'}`} />
              {a.streak > 0 ? `${a.streak} ${plural(a.streak, 'день', 'дня', 'дней')} подряд` : 'без серии'}
            </span>
          )}
          {a.weeklySessions != null && (
            <span className="text-cvx-muted-foreground">{a.weeklySessions} {plural(a.weeklySessions, 'тренировка', 'тренировки', 'тренировок')} / 7 дней</span>
          )}
          {a.adherence != null && <div className="ml-auto"><AdherenceBar value={a.adherence} /></div>}
        </div>
      )}
      {(latest.length > 0 || (a.recentExercises || []).length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {latest.map((r) => (
            <span key={r.scale} className="inline-flex items-center gap-1 rounded-[1rem] bg-cvx-secondary px-2 py-1 text-xs font-semibold text-cvx-secondary-foreground">
              {r.label || SCALE_LABELS[r.scale] || r.scale}: {r.score}
              {typeof r.delta === 'number' && (
                <span className={r.delta >= 0 ? 'text-[oklch(0.5_0.12_150)]' : 'text-[oklch(0.55_0.15_25)]'}>({r.delta >= 0 ? '+' : ''}{r.delta})</span>
              )}
            </span>
          ))}
          {/* Чипы реальных тренировок: что и когда занимался */}
          {(a.recentExercises || []).map((x) => (
            <span key={`ex-${x.slug}`} className="inline-flex items-center gap-1 rounded-[1rem] bg-cvx-primary/10 px-2 py-1 text-xs font-semibold text-cvx-primary">
              {titleOf(x.slug)}{x.date ? <span className="font-medium text-cvx-primary/60">· {x.date}</span> : null}
            </span>
          ))}
        </div>
      )}
      {/* Кнопки ИПР / Отчёты (26.10: вместо «Карточки и шкалы» и «Быстрый тест») */}
      <div className="grid grid-cols-2 gap-2 border-t border-cvx-border pt-3.5">
        <button
          onClick={() => onOpen({ tab: 'ipr' })}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-cvx-ink px-4 py-2.5 text-sm font-semibold text-cvx-primary-foreground transition-colors hover:bg-cvx-ink/90"
        >
          <ClipboardList className="size-4" /> ИПР
        </button>
        <button
          onClick={() => onOpen({ tab: 'reports' })}
          className="inline-flex items-center justify-center gap-1.5 rounded-full border border-cvx-border bg-cvx-card px-4 py-2.5 text-sm font-semibold text-cvx-ink transition-colors hover:bg-cvx-secondary"
        >
          <FileText className="size-4" /> Отчёты
        </button>
      </div>
    </article>
  );
}

// ─────────────────────────── Модал «Добавить пациента» ───────────────────────────

function AddPatientModal({ open, onClose, busy, onSearch, onSend, found, error }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => { if (open) { setEmail(''); setSent(false); } }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Добавить пациента">
      <div className="absolute inset-0 bg-cvx-ink/40" onClick={onClose} />
      <div className="cv-shadow relative w-full max-w-md rounded-3xl border border-cvx-border bg-cvx-card p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-cvx-foreground">Добавить пациента</h2>
            <p className="mt-1 text-sm text-cvx-muted-foreground">Найдите пациента по email и отправьте запрос на связь.</p>
          </div>
          <button onClick={onClose} aria-label="Закрыть" className="rounded-[1.25rem] p-2 text-cvx-muted-foreground hover:bg-cvx-secondary">
            <X className="size-5" />
          </button>
        </div>
        {sent ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="flex size-14 items-center justify-center rounded-full bg-[oklch(0.93_0.05_150)] text-[oklch(0.4_0.1_150)]">
              <CheckCircle2 className="size-7" />
            </span>
            <p className="font-semibold text-cvx-primary">Запрос отправлен</p>
            <p className="text-sm text-cvx-muted-foreground">
              Пациент увидит приглашение в своём кабинете. После подтверждения связь появится в разделе «Пациенты».
            </p>
            <button onClick={onClose} className="mt-2 rounded-full bg-cvx-ink px-5 py-2.5 text-sm font-semibold text-cvx-primary-foreground hover:bg-cvx-ink/90">
              Готово
            </button>
          </div>
        ) : (
          <>
            <form className="mt-5 flex gap-2" onSubmit={(e) => { e.preventDefault(); onSearch(email); }}>
              <input
                type="email" required placeholder="email пациента" value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 flex-1 rounded-[1.25rem] border border-cvx-input bg-cvx-background px-4 text-sm text-cvx-foreground placeholder:text-cvx-muted-foreground focus:outline-none focus:ring-2 focus:ring-cvx-ring"
              />
              <button
                type="submit" disabled={busy}
                className="inline-flex h-12 items-center gap-2 rounded-full bg-cvx-ink px-5 text-sm font-semibold text-cvx-primary-foreground hover:bg-cvx-ink/90 disabled:opacity-60"
              >
                {busy ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />} Найти
              </button>
            </form>
            {error && (
              <p className="mt-3 flex items-center gap-2 text-sm text-cvx-destructive">
                <AlertCircle className="size-4" /> {error}
              </p>
            )}
            {found && (
              <div className="mt-4 flex items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-secondary/50 p-4">
                <Avatar name={found.full_name || found.email} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-cvx-ink">{found.full_name || found.email}</p>
                  <p className="flex items-center gap-1 truncate text-sm text-cvx-muted-foreground">
                    <Mail className="size-3.5" /> {found.email}{found.role && <span> · {found.role === 'patient' ? 'пациент' : 'врач'}</span>}
                  </p>
                </div>
                <button
                  onClick={async () => { await onSend(); setSent(true); }} disabled={busy}
                  className="shrink-0 rounded-full bg-cvx-ink px-4 py-2.5 text-sm font-semibold text-cvx-primary-foreground hover:bg-cvx-ink/90 disabled:opacity-60"
                >
                  Отправить запрос
                </button>
              </div>
            )}
            <p className="mt-4 text-xs leading-relaxed text-cvx-muted-foreground">
              Пациенту нужно подтвердить запрос в личном кабинете. До подтверждения вы не увидите его данные.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── Оболочка кабинета (сайдбар) ───────────────────────────

const SECTIONS = { patients: 'Пациенты', requests: 'Запросы', archive: 'Архив' };

function DoctorShell({ section, counts, onSection, doctorName, specialty, onLogout, children }) {
  const nav = [['patients', Users, counts.patients], ['requests', UserPlus, counts.requests], ['archive', Archive, counts.archive]];
  const isActive = (key) => section === key;
  return (
    <div className="flex min-h-screen flex-col bg-cvx-background lg:flex-row">
      {/* Сайдбар (desktop) */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-cvx-border bg-cvx-sidebar p-5 lg:flex">
        {/* Логотип 1:1 как на лендинге (Cortivox.ai, без подписи «Телереабилитация») */}
        <a href="/" aria-label="Cortivox.ai на главную" className="mb-8 flex items-center gap-2.5 px-2">
          <span style={{ backgroundColor: '#0A0A0A' }} className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white"><BrainCircuit className="size-4" /></span>
          <span style={{ color: '#0A0A0A' }} className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">Cortivox<span className="text-cvx-logo-ai">.ai</span></span>
        </a>
        <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-cvx-muted-foreground">Кабинет</p>
        <nav className="flex flex-col gap-1" aria-label="Разделы кабинета врача">
          {nav.map(([key, Icon, count]) => (
            <button
              key={key}
              onClick={() => onSection(key)}
              aria-current={isActive(key) ? 'page' : undefined}
              className={`flex items-center gap-3 rounded-2xl px-3.5 py-3 text-sm font-semibold transition-colors ${isActive(key) ? 'bg-cvx-ink text-cvx-primary-foreground shadow' : 'text-cvx-secondary-foreground hover:bg-cvx-sidebar-accent'}`}
            >
              <Icon className="size-[18px]" /> {SECTIONS[key]}
              {count > 0 && (
                <span className={`ml-auto inline-flex min-w-6 items-center justify-center rounded-full px-1.5 py-0.5 text-[11px] font-bold ${isActive(key) ? 'bg-cvx-primary-foreground/20 text-cvx-primary-foreground' : 'bg-cvx-accent text-cvx-accent-foreground'}`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-3">
          <div className="flex items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-card p-3">
            <Avatar name={doctorName} size="md" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-cvx-foreground">{doctorName || 'Врач'}</span>
              <span className="flex items-center gap-1 text-xs text-cvx-muted-foreground">
                <Stethoscope className="size-3" /> {specialty}
              </span>
            </span>
          </div>
          {/* «Выйти» — пилюлей, как кнопки лендинга */}
          <button
            onClick={onLogout}
            className="inline-flex items-center justify-center gap-1.5 rounded-full border border-cvx-border bg-cvx-card px-4 py-2.5 text-xs font-semibold text-cvx-ink transition-colors hover:bg-cvx-secondary"
          >
            <LogOut className="size-3.5" /> Выйти
          </button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        {/* Мобильная шапка */}
        <div className="sticky top-0 z-30 border-b border-cvx-border bg-cvx-background/95 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between px-4 pt-3">
            <a href="/" aria-label="Cortivox.ai на главную" className="flex items-center gap-2.5">
              <span style={{ backgroundColor: '#0A0A0A' }} className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white"><BrainCircuit className="size-4" /></span>
              <span style={{ color: '#0A0A0A' }} className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">Cortivox<span className="text-cvx-logo-ai">.ai</span></span>
            </a>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 rounded-full border border-cvx-border bg-cvx-card px-3.5 py-2 text-xs font-semibold text-cvx-ink transition-colors hover:bg-cvx-secondary"
            >
              <LogOut className="size-3.5" /> Выйти
            </button>
          </div>
          <nav className="flex gap-2 overflow-x-auto px-4 py-3" aria-label="Разделы кабинета врача">
            {nav.map(([key, Icon, count]) => (
              <button
                key={key}
                onClick={() => onSection(key)}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${isActive(key) ? 'bg-cvx-ink text-cvx-primary-foreground' : 'border border-cvx-border bg-cvx-card text-cvx-secondary-foreground'}`}
              >
                <Icon className="size-4" /> {SECTIONS[key]}
                {count > 0 && (
                  <span className={`rounded-full px-1.5 text-[11px] font-bold ${isActive(key) ? 'bg-cvx-primary-foreground/20' : 'bg-cvx-accent text-cvx-accent-foreground'}`}>{count}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─────────────────────────── Кабинет врача ───────────────────────────

export default function DoctorCabinetPage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [section, setSection] = useState('patients');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [found, setFound] = useState(null);
  const [addError, setAddError] = useState('');
  // Активность из реальных тренировок: patientId → {lastActiveDays, weeklySessions, totalSessions, ...}
  const [traceAct, setTraceAct] = useState({});
  const [tracesReady, setTracesReady] = useState(false);

  const reload = useCallback(async () => setLinks(await getLinks().catch(() => [])), []);

  useEffect(() => {
    (async () => {
      try {
        const [me, ls] = await Promise.all([getMyProfile(), getLinks()]);
        setProfile(me); setLinks(ls || []);
      } catch (e) { setLoadError(e.message || 'Не удалось загрузить данные'); }
      finally { setLoading(false); }
    })();
  }, []);

  // «Важная информация» на плашках: по каждому активному пациенту считаем
  // активность из его реальных тренировок (listCvTraces) и берём последние
  // результаты ШКАЛ из listScaleReports. Если сервер старый (404) — просто
  // остаётся «—», ничего не падает.
  useEffect(() => {
    if (profile?.role !== 'doctor') return undefined;
    const pids = [...new Set((Array.isArray(links) ? links : [])
      .filter((l) => l.status === 'active')
      .map((l) => l.counterpart?.id || l.patient_id)
      .filter(Boolean))];
    if (pids.length === 0) { setTraceAct({}); setTracesReady(true); return undefined; }
    let alive = true;
    (async () => {
      const entries = await Promise.all(pids.map(async (pid) => {
        const [act, rep] = await Promise.all([
          listCvTraces({ patientId: pid }).then(activityFromTraces).catch(() => null),
          listScaleReports({ patientId: pid }).catch(() => null),
        ]);
        if (!act) return [pid, null];
        const latest = scaleLatestFromReports(Array.isArray(rep) ? rep : Array.isArray(rep?.items) ? rep.items : []);
        return [pid, latest.length ? { ...act, latestResults: latest } : act];
      }));
      if (alive) { setTraceAct(Object.fromEntries(entries)); setTracesReady(true); }
    })();
    return () => { alive = false; };
  }, [profile?.role, links]);

  const act = async (fn, ...args) => {
    setBusy(true); setActionError('');
    try { await fn(...args); await reload(); return true; }
    catch (e) { setActionError(e.message || 'Не удалось выполнить действие'); return false; }
    finally { setBusy(false); }
  };

  const search = async (email) => {
    setFound(null); setAddError(''); setBusy(true);
    try { setFound(await lookupProfile(email)); }
    catch (e) { setAddError(e.message || 'Пользователь не найден'); }
    finally { setBusy(false); }
  };
  const sendRequest = () => act(createLink, found.id).then((ok) => { if (ok) setFound(null); });
  const logout = async () => { await signOut(); navigate('/'); };

  if (loading)
    return (
      <main className="flex min-h-screen items-center justify-center bg-cvx-background">
        <p className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Загрузка…
        </p>
      </main>
    );
  if (profile?.role === 'patient') return <Navigate to="/patient" replace />;
  if (loadError)
    return (
      <main className="flex min-h-screen items-center justify-center bg-cvx-background p-6">
        <div className="max-w-sm text-center">
          <p className="mb-4 text-sm text-cvx-destructive">{loadError}</p>
          <button onClick={() => navigate('/')} className="text-sm underline text-cvx-muted-foreground">Войти заново</button>
        </div>
      </main>
    );

  const meId = profile?.id;
  const active = links.filter((l) => l.status === 'active');
  // Сливаем активность из реальных тренировок в каждую связь (поля API — в приоритете).
  const linksMerged = links.map((l) => {
    if (l.status !== 'active') return l;
    const computed = traceAct[l.counterpart?.id || l.patient_id];
    if (!computed) return l;
    const api = l.activity || {};
    return {
      ...l,
      activity: {
        ...computed,
        lastActiveDays: api.lastActiveDays ?? computed.lastActiveDays,
        streak: api.streak ?? null,
        adherence: api.adherence ?? null,
        latestResults: Array.isArray(api.latestResults) && api.latestResults.length
          ? api.latestResults
          : (Array.isArray(computed.latestResults) ? computed.latestResults : []),
        week: api.week ?? null,
      },
    };
  });
  const activeMerged = linksMerged.filter((l) => l.status === 'active');
  const pending = links.filter((l) => l.status === 'pending');
  const incoming = pending.filter((l) => l?.status === 'pending' && l?.direction === 'incoming');
  const archived = links.filter((l) => l.status === 'rejected' || l.status === 'revoked');
  const counts = { patients: active.length, requests: pending.length, archive: archived.length };
  const weeklySessions = activeMerged.reduce((s, l) => s + (l.activity?.weeklySessions || 0), 0);
  const totalSessions = activeMerged.reduce((s, l) => s + (l.activity?.totalSessions || 0), 0);
  const withAdherence = activeMerged.filter((l) => l.activity?.adherence != null);
  const avgAdherence = withAdherence.length ? Math.round(withAdherence.reduce((s, l) => s + l.activity.adherence, 0) / withAdherence.length) : null;
  // Внимание: не занимался INACTIVE_DAYS+ дней ИЛИ ещё ни разу не занимался.
  const needAttention = activeMerged.filter((l) => l.activity && (l.activity.lastActiveDays == null || l.activity.lastActiveDays >= INACTIVE_DAYS));
  const withStreak = activeMerged.filter((l) => l.activity?.streak != null);
  const bestStreak = withStreak.length ? Math.max(...withStreak.map((l) => l.activity.streak)) : null;

  const visible = (() => {
    let list = activeMerged;
    if (filter === 'risk') list = list.filter((l) => l.activity && (l.activity.lastActiveDays == null || l.activity.lastActiveDays >= INACTIVE_DAYS));
    if (filter === 'new') list = list.filter((l) => l.activity?.lastActiveDays === 0);
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((l) => (l.counterpart?.full_name || '').toLowerCase().includes(q) || (l.counterpart?.email || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => (a.activity?.lastActiveDays ?? 99) - (b.activity?.lastActiveDays ?? 99));
  })();

  const openPatient = (link, opts = {}) => {
    const state = {
      patientProfile: {
        id: link.counterpart?.id || link.patient_id,
        linkId: link.id,
        name: link.counterpart?.full_name,
        email: link.counterpart?.email,
        age: link.counterpart?.age ?? null,
        educationYears: link.counterpart?.education_years ?? link.counterpart?.educationYears ?? null,
        activity: link.activity || null,
      },
      // Кнопки «ИПР»/«Отчёты» на плашке открывают карточку сразу на нужной вкладке
      initialTab: opts.tab || null,
    };
    navigate('/patient', { state });
  };
  return (
    <DoctorShell
      section={section} counts={counts} onSection={setSection}
      doctorName={profile?.full_name || profile?.email || ''} specialty={profile?.specialty || 'Врач'}
      onLogout={logout}
    >
      {section === 'patients' && (
        <div className="flex flex-col gap-6 p-5 lg:p-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-cvx-ink lg:text-3xl">Мои пациенты</h1>
              <p className="mt-1 text-sm text-cvx-muted-foreground">Сводка по активности и быстрый доступ к оценкам и планам</p>
            </div>
            {/* Пилюля как «Запросить демо» на лендинге: форма, цвет, стрелка, подъём при наведении */}
            <button
              onClick={() => setAddOpen(true)}
              style={{ backgroundColor: '#0A0A0A' }} className="inline-flex items-center gap-1.5 rounded-full bg-cvx-ink px-4 py-2.5 text-xs font-semibold text-cvx-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Добавить пациента <ArrowUpRight className="size-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <StatCard icon={Users} label="Активных пациентов" value={active.length} hint="связка подтверждена" />
            <StatCard icon={Activity} label="Тренировок за неделю" value={tracesReady ? weeklySessions : '—'} tone="good"
              hint={avgAdherence != null ? `средняя приверженность ${avgAdherence}%` : `всего тренировок: ${tracesReady ? totalSessions : '—'}`} />
            <StatCard icon={AlertTriangle} label="Требуют внимания" value={tracesReady ? needAttention.length : '—'} tone={needAttention.length > 0 ? 'warn' : 'default'} hint={`нет занятий ${INACTIVE_DAYS}+ дней или ещё не начинали`} />
            <StatCard icon={CalendarDays} label="Лучший streak" value={bestStreak ?? '—'} hint="дней подряд" />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-56 flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-cvx-muted-foreground" />
              <input
                value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск по имени или email…" aria-label="Поиск пациентов"
                className="h-12 w-full rounded-2xl border border-cvx-input bg-cvx-background pl-10 pr-4 text-sm text-cvx-foreground placeholder:text-cvx-muted-foreground focus:outline-none focus:ring-2 focus:ring-cvx-ring"
              />
            </div>
            <div className="flex gap-2" role="tablist" aria-label="Фильтр пациентов">
              {[['all', `Все · ${active.length}`], ['risk', `Внимание · ${needAttention.length}`], ['new', 'Сегодня занимались']].map(([key, label]) => (
                <button
                  key={key}
                  role="tab" aria-selected={filter === key} onClick={() => setFilter(key)}
                  className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${filter === key ? 'bg-cvx-ink text-cvx-primary-foreground' : 'border border-cvx-border bg-cvx-card text-cvx-secondary-foreground hover:bg-cvx-secondary'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-16 text-center">
              <span className="flex size-14 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
                <Search className="size-6" />
              </span>
              <p className="font-semibold text-cvx-primary">Никого не найдено</p>
              <p className="max-w-sm text-sm text-cvx-muted-foreground">Попробуйте изменить запрос или фильтр. Новых пациентов можно добавить по email.</p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {visible.map((l) => <PatientCardItem key={l.id} link={l} onOpen={(opts) => openPatient(l, opts)} />)}
            </div>
          )}
        </div>
      )}

      {section === 'requests' && (
        <div className="flex flex-col gap-6 p-5 lg:p-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-cvx-ink lg:text-3xl">Запросы на связь</h1>
            <p className="mt-1 text-sm text-cvx-muted-foreground">Подтверждённые пациенты появятся в разделе «Пациенты»</p>
          </div>
          {actionError && <p className="rounded-[1.25rem] bg-red-50 px-4 py-2.5 text-xs text-red-600">{actionError}</p>}
          {[['Входящие', incoming, true], ['Исходящие', pending.filter((l) => !(l?.status === 'pending' && l?.direction === 'incoming')), false]].map(([title, list, isIncoming]) => (
            <section key={title} className="flex flex-col gap-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-cvx-muted-foreground">{title} · {list.length}</h2>
              {list.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-12 text-center">
                  <span className="flex size-12 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
                    <UserPlus className="size-5" />
                  </span>
                  <p className="text-sm text-cvx-muted-foreground">{isIncoming ? 'Новых входящих запросов нет' : 'Вы ещё не отправляли запросов'}</p>
                </div>
              ) : (
                list.map((l) => {
                  const name = l.counterpart?.full_name || l.counterpart?.email || l.patient_id;
                  return (
                    <div key={l.id} className="cv-shadow flex flex-wrap items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-card p-4">
                      <Avatar name={name} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-cvx-foreground">{name}</p>
                        <p className="truncate text-sm text-cvx-muted-foreground">{l.counterpart?.email || l.patient_id}</p>
                      </div>
                      {isIncoming ? (
                        <div className="flex gap-2">
                          <button onClick={() => act(acceptLink, l.id)} disabled={busy} className="rounded-full bg-cvx-ink px-4 py-2.5 text-sm font-semibold text-cvx-primary-foreground hover:bg-cvx-ink/90 disabled:opacity-60">Принять</button>
                          <button onClick={() => act(rejectLink, l.id)} disabled={busy} className="rounded-full border border-cvx-border px-4 py-2.5 text-sm font-semibold text-cvx-ink hover:bg-cvx-secondary disabled:opacity-60">Отклонить</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-cvx-muted-foreground">
                          <Loader2 className="size-3.5 animate-spin" /> Ожидает подтверждения пациентом
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </section>
          ))}
        </div>
      )}

      {section === 'archive' && (
        <div className="flex flex-col gap-6 p-5 lg:p-8">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-cvx-ink lg:text-3xl">Архив</h1>
            <p className="mt-1 text-sm text-cvx-muted-foreground">Архивированные и отклонённые связи. Данные сохраняются.</p>
          </div>
          {archived.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-3xl border border-dashed border-cvx-border bg-cvx-card/60 py-12 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-cvx-secondary text-cvx-muted-foreground">
                <Archive className="size-5" />
              </span>
              <p className="text-sm text-cvx-muted-foreground">Архив пуст</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {archived.map((l) => {
                const name = l.counterpart?.full_name || l.counterpart?.email || l.patient_id;
                return (
                  <div key={l.id} className="cv-shadow flex flex-wrap items-center gap-3 rounded-2xl border border-cvx-border bg-cvx-card p-4">
                    <Avatar name={name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-cvx-foreground">{name}</p>
                      <p className="truncate text-sm text-cvx-muted-foreground">{l.counterpart?.email}</p>
                    </div>
                    <button
                      onClick={() => act(createLink, l.counterpart?.id || l.patient_id)} disabled={busy}
                      className="rounded-full border border-cvx-border px-4 py-2.5 text-sm font-semibold text-cvx-ink hover:bg-cvx-secondary disabled:opacity-60"
                    >
                      Восстановить
                    </button>
                    <ChevronRight className="size-4 text-cvx-muted-foreground/40" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <AddPatientModal
        open={addOpen}
        onClose={() => { setAddOpen(false); setFound(null); setAddError(''); }}
        busy={busy} onSearch={search} onSend={sendRequest} found={found} error={addError}
      />
    </DoctorShell>
  );
}
